import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { IOrganisationsHierarchy, OrganisationDataService } from '@services/organisation-data.service';
import { distinctUntilChanged, filter, finalize, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, firstValueFrom, interval, Subject } from 'rxjs';
import { DatePickerPresets, GlobalFunctions, ImageErrorPath, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { LevelAsset, AssetsDataService } from '@services/assets-data.service';
import { DatapointsDataService, DataPoint, AssetDataPoint, ISensorData } from '@services/datapoints-data.service';
import { ReportPDFService } from '@services/report-data.service';
import {
	AxisModel,
	ChartComponent, ExportType, IAxisLabelRenderEventArgs, IDragCompleteEventArgs, ILoadedEventArgs,
	ISelectionCompleteEventArgs, SeriesModel, StripLineSettingsModel, TooltipSettingsModel
} from '@syncfusion/ej2-angular-charts';
import { Browser } from '@syncfusion/ej2-base';
import { DropDownList } from '@syncfusion/ej2-dropdowns';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { EditSensorDatapointComponent } from 'src/app/dialogs/edit-sensor-datapoint/edit-sensor-datapoint.component';
import { Rule, RuleDefinition, RulesDataService } from '@services/rules-data.service';
import { EventsDataService, RuleEvent } from '@services/events-data.service';
import { Grid } from '@syncfusion/ej2-angular-grids';
import { Account, AccountFeatures, AccountsService, IUserPreferences } from '@services/accounts.service';
import { AuthenticationService, IWebAppUser, WebAppUser } from '@services/authentication.service';
import { SidebarComponent } from '@syncfusion/ej2-angular-navigations';
import { ButtonComponent } from '@syncfusion/ej2-angular-buttons';
import { CreateActionComponent, createActionDialogData } from 'src/app/dialogs/create-action/create-action.component';
import { Action, ActionsDataService } from '@services/actions-data.service';
import { AssetModelsComponent } from 'src/app/dialogs/asset-models/asset-models.component';
import { WatchListComponent } from '@components/watch-list/watch-list.component';
import { ActionListDialogData, AssetActionsComponent } from 'src/app/dialogs/asset-actions/asset-actions.component';
import { AssetHistoryComponent } from 'src/app/dialogs/asset-history/asset-history.component';
import { RuleListComponent, RuleListDialogData } from 'src/app/dialogs/rule-list/rule-list.component';
import { EditAssetDatapointsComponent, EditAssetSettingsDialogData } from 'src/app/dialogs/edit-asset-datapoints/edit-asset-datapoints.component';
import { AssetInformationComponent, AssetInformationDialogData } from 'src/app/organisations/hierarchy/asset-information/asset-information.component';
import { VirtualSensor, VirtualSensorsService } from '@services/virtual-sensors.service';
import { CreateCmmsCaseComponent, createCMMSDialogData } from 'src/app/dialogs/create-cmms-case/create-cmms-case.component';
import { CaseFile, CmmsService, ICaseFile } from '@services/cmms.service';

/**
 * Component
 * AssetChartsComponent is an Angular component that displays charts for asset data points, 
 * it allows users to view and analyze sensor data, configure chart settings, and interact with the 
 * chart through various features such as zooming, tooltips, and exporting data. The component also 
 * integrates with other services and dialogs to provide additional functionality such as editing sensor data points, 
 * creating actions, viewing asset information, and managing rules and events related to the assets. 
 * It utilizes the Syncfusion charting library for rendering the charts and provides a responsive and 
 * interactive user interface for analyzing asset data.
 */
@Component({
	selector: 'app-asset-charts',
	templateUrl: './asset-charts.component.html',
	styleUrls: ['./asset-charts.component.scss']
})
export class AssetChartsComponent implements OnInit, OnDestroy {

	/** imageDatapointPath */
	// public imageDatapointPath(): string {
	// 	return imagePath(this.selectedAccountUID, imgTypes.datapoints).toLowerCase()
	// }
	// /** imagePathSensors */
	// public imagePathSensors(): string {
	// 	return imagePath(this.selectedAccountUID, imgTypes.sensors).toLowerCase()
	// }

	/** imagePathSensors */
	public imagePathSensors(dp: AssetDataPoint): string {
		try {
			if (!dp || !dp.SensorID || !dp.Modified) {
				return ImageErrorPath; // Return a default error image path if dp is undefined or missing properties
			}
			return imagePath(this.selectedAccountUID, imgTypes.sensors).toLowerCase() + dp.SensorID + "?" + dp.Modified;
		} catch (error) {
			console.error('Error occurred while generating image path for sensor:', error);
			return ImageErrorPath;
		}
	}

	/** param1 */
	public param1: string = '';
	/** param2 */
	public param2: string = '';
	/** param3 */
	public param3: string = '';
	/** param4 */
	public param4: string = '';
	/** Spinner */
	public spinner: boolean
	/** Controls the visibility of the PDF Button Spinner */
	public generatingPDF: boolean = false
	/** Controls the visibility of the Excel Button Spinner */
	public generatingExcel: boolean = false

	/** Property to hold the sensor name */
	@Input() assetID: string;

	/** Selected Organisation ID */
	@Input() selectedOrganisationUID: string;

	/** Selected Hierarchical Location */
	public selectedHierarchicalLocation: IOrganisationsHierarchy;

	/** paramAssetID */
	public paramAssetID: any;

	/** Selected Asset */
	public selectedAsset: LevelAsset;

	/** Data Points */
	public dataPoints: AssetDataPoint[];
	@Input() inputDataPoints: AssetDataPoint[];
	@Output() outputDataPoints: EventEmitter<AssetDataPoint[]> = new EventEmitter();

	/** Asset Datapoint IDs as a list */
	public dataPointIDs: string[];

	/** Data Point Values */
	public dataPointValues: object[];

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Observable Subject Finalise */
	private chartFinalise = new Subject<void>();

	/** chartContainer */
	@ViewChild('chartContainer', { static: false }) chartContainer: ElementRef;

	/** My Chart */
	@ViewChild('myChart') public myChart: ChartComponent;

	/** Datapoint SideNav */
	@ViewChild('datapointSidenav') public dpSidenav: MatSidenav;

	/** resizeObserver */
	private resizeObserver: ResizeObserver;

	/** Chart Zoom Settings */
	public zoomSettings: Object;
	/** Sensor X Axis */
	public sensorXAxis: Object;
	/** Sensor Y Axis */
	public sensoryAxis: object;
	/** Sensor Y Average Axis */
	public sensoryAxis_Avg: object

	/** Sensor pen Pallet Colours */
	public palette: object;

	/** Sensor alias Name */
	public chartSensorAlias: string = ''
	/** Sensor Description */
	public chartSensorDesc: string = ''
	/** Sensor Chart title */
	public chartTitleSensors: string = ''

	/** Chart Legend */
	public legend: Object;
	/** Sensor Data Label */
	public dataLabels: Object;
	/** Tooltip Settings */
	public tooltipSettings: Object;
	/** Chart boarder Settings */
	public borderSettings: Object;
	/** DataPoint maker appearance */
	public marker_1: Object;
	/** DataPoint maker appearance */
	public marker_2: Object;
	/** DataPoint maker appearance */
	public marker_3: Object;
	/** DataPoint maker appearance */
	public marker_4: Object;
	/** DataPoint maker appearance */
	public marker_5: Object;
	/** Chart Text Style */
	public titleStyle: Object;

	/** crosshair */
	public crosshair: object = {
		enable: true,
		lineType: 'Vertical'
	};

	/** Chart Units */
	public chartUnits: string

	/** Sensor Data for selected Sensor */
	public sensorData: Object[];
	/** Sensor Data for Comparison Sensor 1 */
	public sensorData_1: Object[];
	/** Sensor Data for Comparison Sensor 2 */
	public sensorData_2: Object[];
	/** Sensor Data for Comparison Sensor 3 */
	public sensorData_3: Object[];
	/** Sensor Data for Comparison Sensor 4 */
	public sensorData_4: Object[];
	/** Sensor Data for Comparison Sensor 5 */
	public sensorData_5: Object[];
	/** Sensor Data for Comparison Sensor 6 */
	public sensorData_6: Object[];
	/** Sensor Data for Comparison Sensor 7 */
	public sensorData_7: Object[];
	/** Sensor Data for Comparison Sensor 8 */
	public sensorData_8: Object[];
	/** Sensor Data for Comparison Sensor 9 */
	public sensorData_9: Object[];
	/** Sensor Data for Comparison Sensor 10 */
	public sensorData_10: Object[];

	/** Sensor Data for Sensor 1 Running Average */
	public sensorData_1_RunningAvg: Object[];
	/** Sensor Data for Sensor 1 Running Average */
	public sensorData_1_PeriodAvg: Object[];

	/** Sensor Data for Sensor 1 Running */
	public sensorData_1_Running: object[];

	/** Sensor 1 Running Average */
	public chartSensor_1_RunningAvg: any
	/** Sensor 1 Period Average */
	public chartSensor_1_PeriodAvg: any

	/** Sensor 1 Running */
	public chartSensor_1_Running: any

	/** Sensor Serial Number */
	public chartSensor: any
	/** Serial Number Comparison Sensor 1 */
	public chartSensor_1: any
	/** Serial Number Comparison Sensor 2 */
	public chartSensor_2: any
	/** Serial Number Comparison Sensor 3 */
	public chartSensor_3: any
	/** Serial Number Comparison Sensor 4 */
	public chartSensor_4: any
	/** Serial Number Comparison Sensor 5 */
	public chartSensor_5: any
	/** Serial Number Comparison Sensor 6 */
	public chartSensor_6: any
	/** Serial Number Comparison Sensor 7 */
	public chartSensor_7: any
	/** Serial Number Comparison Sensor 8 */
	public chartSensor_8: any
	/** Serial Number Comparison Sensor 9 */
	public chartSensor_9: any
	/** Serial Number Comparison Sensor 10 */
	public chartSensor_10: any

	/** pointMode */
	public pointMode: DropDownList;
	/** modeValue */
	public modeValue: string = "Drop";

	/** exportType */
	public exportType: DropDownList;
	/** maxMonths */
	private maxMonths: number = 36; // max months increased as retrieving data for long periods no longer an issue

	/** showCard */
	public showCard: boolean = false; // Variable to toggle card visibility

	/** Property to store the selected export type */
	public selectedExportType: string = 'CSV';
	/** Property to store the selected export type */
	public selectedExportFilename: string = '';
	/** strDuration */
	public strDuration: string = ''; //'00:12:00';
	/** Define start date */
	public startDate: Date;
	/** Define end date */
	public endDate: Date;
	/** paramStartDate */
	public paramStartDate: Date;
	/** paramEndDate */
	public paramEndDate: Date;

	public visibleStartDate: Date;
	public visibleEndDate: Date;

	/** Tooltip Example */
	public tooltip: TooltipSettingsModel = {
		enable: true,
		// tslint:disable-next-line:max-line-length
		template:
			'<div id="Tooltip">' +
			'<table style="width:100%;  border: 1px solid black;" class="table-borderless">' +
			'<tr>' +
			'<th rowspan="2" style="background-color: #C1272D">' +
			'<img src="https://el-watch.com/wp-content/uploads/elementor/thumbs/NeuronVibration-qixmxckdc9i0q3gi9tyx80jn9b8melzhv22f1puf1c.png" style="height:100px; width:100px;padding:0px" />' +
			// tslint:disable-next-line:max-line-length
			'</th>' +
			'<td style="height: 25px; width: 50px; background-color: #C1272D; font-size: 14px; color: #E7C554; font-weight: bold; padding-left: 5px">${x}</td>' +
			'</tr>' +
			// tslint:disable-next-line:max-line-length
			'<tr>' +
			'<td style="height: 25px; width: 100px; background-color: #C1272D; font-size: 18px; color: #FFFFFF; font-weight: bold; padding-left: 5px">${y}</td>' +
			'</tr>' +
			'</table>' +
			'</div>'
	};

	/**
	 * Chart Selection mode
	 * None|Point|Series|Cluster|DragXY|DragX|DragY
	 */
	public selectionMode: string;

	/** this User */
	public thisUser: IWebAppUser;

	/** Selected Account ID */
	public selectedAccountUID: string

	/** used for displaying min and max for datapoints */
	public uniqueDPs: any[];

	/** averagedData */
	public averagedData: { datapointID: number, average: number, runningAvg: number }[] = [];

	/** rules */
	public assetRules: Rule[];

	/** stopCheckingAvg */
	public stopCheckingAvg: boolean;

	/** fields */
	public fields: Object = { text: 'text', value: 'index' };

	/** selections */
	public selections: { text: string, index: number }[] = [
		{ text: "Chart DataPoints", index: 0 },
		{ text: "Rules", index: 1 }
	]

	/** selectedItem */
	public selectedItem: number = 0;

	/** selectedRule */
	public selectedRule: Rule | undefined;

	/** parsedData */
	public parsedData: ISensorData[] = [];

	/** selectedRules */
	public selectedRules: Rule[] = [];

	/** selectedDatapoints */
	public selectedDatapoints: AssetDataPoint[] = []

	// Define colors as private properties of the class
	private lightGreenCyan: string = "#00E1B3";         // Lighter green/cyan color
	private lightGreenCyanDarker1: string = "#02735c";  // Noticeably darker version of light green/cyan
	private lightGreenCyanDarker2: string = "#015748";  // Even darker version of light green/cyan

	private darkBlue: string = "#3366FF";               // Slightly lighter dark blue color
	private darkBlueDarker1: string = "#022073";        // Darker version of dark blue
	private darkBlueDarker2: string = "#4b0073";        // Even darker version of dark blue

	// Predefined array of default colors
	private defaultColors: string[] = [
		"#FF5733", // Red
		"#33FF57", // Green
		"#3357FF", // Blue
		"#F0E130", // Yellow
		"#FF33F0", // Pink
		"#33FFF6", // Aqua
		"#FF9833", // Orange
		"#F633FF", // Purple
		"#8BFF33"  // Light Green
	];

	// Variable to track the next default color to use
	private defaultColorIndex: number = 0;

	private seriesColors: { datapointID: number, color: string }[] = [];

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/** Scroll Position */
	private scrollPosition: any = [0, 0]

	/** getNewDps */
	private getNewDps: boolean = false;

	/** RebuildChart */
	private RebuildChart: boolean = false;

	/** dockContent */
	private dockContent: string = "dockContent";

	/** Page Refresh Interval object */
	private refreshInterval: any;

	/** Page Refresh Time in Minutes */
	private refreshTime: number = 2 // Minutes

	/** showOffline */
	public showOffline: boolean = false;

	/** filterSettings */
	public filterSettings: Object;

	/** toolbar */
	public toolbar: string[];

	/** editSettings */
	public editSettings: Object;

	/** timestampRules */
	public timestampRules: Object;

	/** gridData */
	public gridData: Object[] = [];

	/** loadingIndicator */
	public loadingIndicator: Object;

	/** selectedAccount */
	public selectedAccount: Account;

	/** showTabulateOption */
	public showTabulateOption: boolean = false;

	/** tabulate */
	public tabulate: boolean = false;

	/** Grid */
	@ViewChild('Grid') public Grid: Grid;

	/** enabledDatapoints */
	public enabledDatapoints: { Datapoint: AssetDataPoint, Enabled: boolean }[] = [];

	/** updateGridColumns */
	public updateGridColumns: boolean = false;

	/** gridColumns */
	public gridColumns: Object;

	/** dataChartMaximums - 0 is primary y axis and each number after is the remaining axes*/
	public dataChartMaximums: { axis: number, maximum: any, interval: any }[] = [];

	/** HilightedSeries */
	public HilightedSeries: DataPoint | undefined;

	/** HilightedRule */
	public HilightedRule: Rule | undefined;

	/** regex */
	public regex = /^\[[0-9]*\]$/;

	/** regexDelta */
	public regexDelta = /^\[[0-9]*_DELTA\]$/;

	/** shouldAddSecondXAxis */
	public shouldAddSecondXAxis: boolean = false;

	/** showDeltaAsBar */
	public showDeltaAsBar: boolean = true;

	/** barData */
	public barData: { datapointID: any, data: ISensorData[], min: number, max: number }[] = [];

	/** calculatedData */
	public calculatedData: { datapointID: any, min: number, max: number }[] = [];

	/** includesCumulativeDatapoints */
	public includesCumulativeDatapoints: boolean = false;

	/** AccountFeatures */
	public AccountFeatures = AccountFeatures;

	/** assetVirtualSensors */
	public assetVirtualSensors: VirtualSensor[] = [];

	/** virtualSensorInputDataPoints */
	public virtualSensorInputDataPoints: DataPoint[] = [];

	/** virtualSensorData */
	public virtualSensorData: ISensorData[] = [];

	/** today - #71 */
	public today: Date = new Date();

	/** datePickerPresets - #71 */
	public datePickerPresets = DatePickerPresets;

	/**
	 * Default Constructor
	 * @param dialog MatDialog
	 * @param route route
	 * @param router router
	 * @param OrganisationData OrganisationData
	 * @param AssetsDataService AssetsDataService
	 * @param rulesDataService rulesDataService
	 * @param DatapointsDataService DatapointsDataService
	 * @param eventsDataService eventsDataService
	 * @param siteSubscriptions siteSubscriptions
	 * @param globalFunctions globalFunctions
	 * @param ReportPDFService ReportPDFService
	 * @param authenticationService authenticationService
	 * @param actionsDataService actionsDataService
	 * @param cmmsService cmmsService
	 */
	constructor(
		public dialog: MatDialog,
		private route: ActivatedRoute,
		private router: Router,
		public OrganisationData: OrganisationDataService,
		public AssetsDataService: AssetsDataService,
		public rulesDataService: RulesDataService,
		public DatapointsDataService: DatapointsDataService,
		public eventsDataService: EventsDataService,
		public siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions,
		private ReportPDFService: ReportPDFService,
		private authenticationService: AuthenticationService,
		public actionsDataService: ActionsDataService,
		private virtualSensorsService: VirtualSensorsService,
		private datapointsDataService: DatapointsDataService,
		public cmmsService: CmmsService
	) {
		combineLatest([
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParams.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			withLatestFrom(
				this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedAsset$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedHierarchicalLocation$.pipe(takeUntil(this.finalise), distinctUntilChanged())
			),
			filter(([[paramMap, queryParams], selectedAccountGUID, selectedAccount,
				selectedOrganisationUID, selectedAsset, currentUser, selectedHierarchicalLocation]) =>
				!!paramMap && paramMap.has('assetID') && !!queryParams &&
				!!selectedAccountGUID && !!selectedAccount && !!selectedOrganisationUID &&
				!!selectedAsset && !!currentUser && !!selectedHierarchicalLocation
			)
		).subscribe({
			next: ([[paramMap, queryParams], selectedAccountGUID, selectedAccount, selectedOrganisationUID,
				selectedAsset, currentUser, selectedHierarchicalLocation]) => {

				this.thisUser = currentUser;

				this.selectedAccountUID = selectedAccountGUID;

				this.selectedOrganisationUID = selectedOrganisationUID;

				this.selectedHierarchicalLocation = selectedHierarchicalLocation;

				this.selectedAccount = selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;

				if (this.selectedAccount.Features.find((x: any) => x.FeatureID === 3)) {
					this.showTabulateOption = true;
				}

				const assetID = parseInt(paramMap.get('assetID') ?? "", 0);

				this.defaultColorIndex = 0;

				this.seriesColors = [];

				// Check if we are ona new Asset, If So rebuild Chart
				this.RebuildChart = (this.paramAssetID == assetID);

				/** if the asset hasnt changed but there are no query params we dont make any network requests,
				 * used because changing query params and params at the same time causes everything to fire twice
				 */
				let dontCheck: boolean = false;

				if (this.paramAssetID == assetID) {
					dontCheck = true;
				}

				/* fix for issue #87 */
				if (JSON.stringify(queryParams) != "{}") this.getNewDps = false;

				let Preferences = JSON.parse(this.thisUser.Preferences ?? "{}") as IUserPreferences;

				if (this.paramAssetID != assetID) {
					this.getNewDps = true;
				}
				
				if (this.strDuration === "" || this.strDuration === "custom" || parseInt(this.strDuration.split(":")[0]) > 7) {
					this.strDuration = Preferences.TrendDuration || "07:00:00";
				}

				this.paramAssetID = assetID;

				this.uniqueDPs = [];

				let parts: any[] = this.strDuration.split(":");
				this.endDate = new Date();
				this.startDate = new Date();
				this.startDate.setDate(this.startDate.getDate() - parts[0]);
				this.startDate.setHours(this.startDate.getHours() - parts[1]);
				this.startDate.setMinutes(this.startDate.getMinutes() - parts[2]);

				if (queryParams['from']) {
					this.paramStartDate = new Date(Number(queryParams['from']));
					this.paramEndDate = new Date(Number(queryParams['to']));
					this.startDate = this.paramStartDate;
					this.endDate = this.paramEndDate;
				}

				if (queryParams['duration']) {
					this.strDuration = queryParams['duration'];
				}

				this.visibleStartDate = this.startDate;
				this.visibleEndDate = this.endDate;

				if (!dontCheck) {
					if (this.router.url.includes("events")) {
						this.selectedAsset = selectedAsset;
					}
					else if (this.router.url.includes(selectedAsset.ParentUID)) {
						this.selectedAsset = selectedAsset;
					}
					else if (!selectedAsset.ParentUID) {
						this.selectedAsset = selectedAsset;
					}
					this.getRules(this.selectedAsset);
				}
				this.GetOrganisationAssets(this.paramAssetID, this.startDate.getTime(), this.endDate.getTime());

				if (queryParams['from']) {
					this.resetZoom();
				}

				this.disableChartRefresh();
				this.unzoomChart();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: combined route parameters", false);
			}
		});
	}

	/**
	 * ngOnInit
	 * Used to initialize the component, it sets up the x-axis for the chart, initializes marker settings for data points, configures zoom settings, legend visibility, tooltip settings, and selection mode for the chart. 
	 * It also subscribes to observables for refreshing the chart and clearing and refreshing the chart, 
	 * where it defines the logic to execute when these events occur, such as fetching new data points and refreshing the chart display. 
	 * Additionally, it initializes a DropDownList for export types and sets up filter settings, toolbar options, edit settings, timestamp rules, and loading indicators for the component.
	 */
	ngOnInit(): void {
		this.setXAxis();

		this.marker_1 = { visible: false }
		this.marker_2 = { visible: false }
		this.marker_3 = { visible: false }
		this.marker_4 = { visible: false }
		this.marker_5 = { visible: false }

		//Initializing Marker
		this.marker_1 = {
			visible: true, width: 8, height: 8,
			shape: 'Diamond', dataLabel: { name: 'x' }, isFilled: true
		};

		this.zoomSettings = {
			mode: 'X',
			// enableMouseWheelZooming: true,
			enablePinchZooming: true,
			enableSelectionZooming: true,
			enableScrollbar: true
		};

		this.legend = { visible: true };
		this.tooltipSettings = {
			enable: true,
			fadeOutDuration: 1000,
			fadeOutMode: "Click"
			//shared: true // Enables shared tooltip
		};
		this.selectionMode = 'DragX';

		this.siteSubscriptions.refreshChart$.pipe(takeUntil(this.finalise), distinctUntilChanged())
			.subscribe({
				next: (refresh) => {
					if (refresh) {
						this.disableChartRefresh();
						this.seriesColors = [];
						this.defaultColorIndex = 0;
						this.getDPVs(this.startDate.getTime(), this.endDate.getTime());
						this.siteSubscriptions.RefreshChart(false);
						this.enableChartRefresh();
					}
				}
			})

		this.siteSubscriptions.clearAndRefreshChart$.pipe()
			.subscribe({
				next: (refresh) => {
					if (refresh) {
						const scrollableDiv = document.getElementById('dockContent');
						const scrollX = scrollableDiv?.scrollLeft || 0;
						const scrollY = scrollableDiv?.scrollTop || 0;

						this.dataPoints = [];
						this.dataPointIDs = [];

						this.myChart.series.forEach(series => {
							series.dataSource = [];
						});
						this.myChart.dataSource = [];
						this.myChart.clearSeries();
						this.myChart.refresh();
						this.myChart.dataBind();
						this.gridLoad(true);

						if (scrollableDiv) {
							scrollableDiv.scrollLeft = scrollX;
							scrollableDiv.scrollTop = scrollY;
						}
						this.siteSubscriptions.ClearAndRefreshChart(false);
					}
				}
			})

		this.exportType = new DropDownList({
			index: 0,
			width: 90,
		});
		this.exportType.appendTo('#exporttype');
		// this.numberRND = Math.random()		

		this.filterSettings = { type: 'Excel' };
		this.toolbar = ['Add', 'Edit', 'Delete', 'Update', 'Cancel'];
		this.editSettings = { allowEditing: false, allowAdding: false, allowDeleting: false };
		this.timestampRules = { required: true, number: true };
		this.loadingIndicator = { indicatorType: 'Shimmer' };
	}

	/**
	 * ngAfterViewInit
	 * Used to set up a ResizeObserver that listens for changes in the size of the chart container element, 
	 * it creates a new ResizeObserver instance and defines a callback function that is triggered whenever the 
	 * size of the observed element changes. Inside the callback, it calls the refresh method on the myChart instance 
	 * to update the chart's layout and appearance based on the new size. Finally, it starts observing the 
	 * chartContainer element for any size changes. This ensures that the chart remains responsive and visually 
	 * consistent when the container is resized.
	 */
	ngAfterViewInit() {
		this.resizeObserver = new ResizeObserver(() => {
			this.myChart.refresh();
		});
		this.resizeObserver.observe(this.chartContainer.nativeElement);
	}

	/**
	 * ngOnDestroy
	 * Used to perform cleanup when the component is destroyed, it attempts to unsubscribe from all active subscriptions by 
	 * emitting a value and completing the finalise subject, disables automatic chart refreshing, 
	 * completes the chartFinalise subject to clean up any related subscriptions, and disconnects the resize observer if it exists. 
	 * The try-catch block is used to handle any potential errors that may occur during this cleanup process gracefully.
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();

			this.disableChartRefresh();

			this.chartFinalise.next();
			this.chartFinalise.complete();

			if (this.resizeObserver) {
				this.resizeObserver.disconnect();
			}
		}
		catch { }
	}

	/**
	 * resetZoom
	 * Used to reset the zoom level of the chart to its default state, it checks if the chart is currently zoomed in 
	 * using the zoomModule's isZoomed property. If the chart is zoomed in, it would execute the necessary logic to reset the zoom level, 
	 * allowing the user to view the entire chart again. However, the actual implementation of resetting the zoom is currently commented out 
	 * and marked as "ToDo!!!", indicating that this functionality is intended to be added in the future.
	 */
	resetZoom(): void {
		// if (this.myChart.zoomModule.isZoomed) {
		// 	//ToDo!!!
		// }
	}

	/** 
	 * disableChartRefresh
	 * Used to disable the automatic refreshing of the chart data, it emits a value to stop the interval and completes the subject to clean up
	 */
	disableChartRefresh() {
		// console.log("Disable Chart Refresh");
		this.chartFinalise.next();  // Emit a value to stop the interval
		this.chartFinalise.complete();  // Complete the subject to clean up

		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);  // Clear the interval if it's set
			this.refreshInterval = null;  // Reset to null to prevent memory leaks
		}

		// Reinitialize chartFinalise for the next call
		this.chartFinalise = new Subject<void>();
	}

	/** 
	 * enableChartRefresh
	 * Used to enable automatic refreshing of the chart data at regular intervals, 
	 * it creates an RxJS interval that emits a value every specified number of minutes (defined by the refreshTime property), 
	 * and subscribes to this interval. On each emission, it checks if the chart is currently zoomed in; 
	 * if it is not zoomed, it further checks the current duration string (strDuration) against a set of predefined short durations. 
	 * If the duration matches one of these short durations, it calls the selectTime method with the current duration to refresh the chart data. 
	 * If the chart is zoomed in or if the duration is too long, it does not refresh the data and logs a message to the console 
	 * indicating that the chart will not be refreshed due to these conditions.
	 */
	enableChartRefresh() {
		// console.log("Enable Chart Refresh")
		// Create an RxJS interval that emits a value every 10 minutes (600,000 milliseconds).
		this.refreshInterval = interval((this.refreshTime * 60000)).pipe(takeUntil(this.chartFinalise)).subscribe(() => {

			if (!this.myChart.zoomModule.isZoomed) {
				switch (this.strDuration) {
					case "00:00:30":
					case "00:01:00":
					case "00:04:00":
					case "00:12:00":
						// console.log('Refreshing Chart Data: ' + Date());
						this.selectTime(this.strDuration)
						break;
					default:
						// console.log('Time Period too large for Refresh: ' + Date());
						break;
				}
			} else {
				// console.log('Chart Zoomed - Do not Refresh: ' + Date());
			}
		});
	}

	/**
	 * toggleCardVisibility
	 * Used to toggle the visibility of a card element in the user interface, it negates the current value of the showCard property, which is a boolean that controls whether the card is displayed or hidden. When this method is called, it will switch the state of showCard from true to false or from false to true, effectively showing or hiding the card in the UI based on its previous state.
	 */
	toggleCardVisibility() {
		// this.numberRND = Math.random()
		this.showCard = !this.showCard; // Toggle the value of showCard
	}

	/**
	 * generatePDF
	 * Used to generate a PDF report for the selected asset and date range, it creates a local Subject to control the subscription lifecycle, shows a spinner while the PDF is being generated, calls the getReportPDF$ method of the ReportPDFService with the necessary parameters, and subscribes to the response. If the response is successful, it determines the MIME type of the response and creates a downloadable file (PDF or Excel) based on that MIME type. If there is an error during the process, it displays an error message using a snack bar and logs the error to the console. Finally, it ensures that the subscription is properly terminated and the spinner is hidden once the process is complete.
	 */
	generatePDF() {

		// console.log("IM TRYING TO RUN")
		this.ShowPDFSpinner(true)

		this.ReportPDFService.getReportPDF$(this.assetID, this.startDate.getTime(), this.endDate.getTime(), this.selectedAccountUID, this.selectedOrganisationUID).pipe(
			take(1), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => { this.ShowPDFSpinner(false) })
		).subscribe({
			next: (response) => {
				// Determine the MIME type and handle accordingly
				const mimeType = response.type || 'application/pdf'; // Default to PDF if type is not provided
				let fileName = 'sensorReport';

				if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
					fileName += '.xlsx';
				} else if (mimeType === 'application/pdf') {
					fileName += '.pdf';
				} else {
					fileName += '.txt'; // Default to txt for unknown types
				}

				let anchor = document.createElement('a');
				const blob = new Blob([response], { type: mimeType });
				const url = window.URL.createObjectURL(blob);

				anchor.download = fileName;
				anchor.href = url;
				anchor.click();
			},
			error: (error) => {
				this.globalFunctions.DisplaySnackBar({ message: "Error Creating PDF Document", type: SnackType.Error, action: "", duration: 2000 })
				console.error('Error calling Azure function:', error);
			}
		});
	}


	/**
	 * DatePickerChange
	 * Used to handle changes in the date range selected by the user for displaying sensor data on the chart, it takes an event object as input which contains the new start and end dates selected by the user, it validates that the selected date range is less than a specified maximum number of months (defined by the maxMonths property) to prevent performance issues when retrieving and displaying large amounts of data, if the date range is valid it updates the startDate and endDate properties with the new values, adjusts the endDate to include the last day of the range, resets the duration search to "custom", updates the visible start and end dates for the chart, calls the setXAxis method to adjust the X-axis configuration based on the new date range, and finally rebuilds the URL with the new duration and date range to allow for bookmarking or sharing of specific views of the chart based on the selected time range
	 * the method includes error handling to catch and log any exceptions that may occur during the process, ensuring that the application remains stable and provides feedback to the user in case of invalid input or other issues
	 * @param event
	 */
	DatePickerChange(event: any) {
		try {
			// Validate the Date Range < 6 Months
			// Validate the Date Range < 6 Months
			const date1 = new Date(event.startDate.getTime());
			const date2 = new Date(event.endDate.getTime());

			const diffInMs = Math.abs(date2.getTime() - date1.getTime());
			const diffInMonths = diffInMs / (1000 * 60 * 60 * 24 * 30.44);

			if (diffInMonths < this.maxMonths) {
				// console.log('The difference is less than ' + this.maxMonths + ' months');

				this.startDate = event.startDate;
				this.endDate = event.endDate
				/** Add a day to retrieve data for last day too */
				this.endDate.setDate(this.endDate.getDate() + 1)

				/** Reset Duration Search */
				this.strDuration = "custom";

				this.visibleStartDate = this.startDate;
				this.visibleEndDate = this.endDate;

				this.setXAxis();

				this.rebuildURL(this.strDuration, this.startDate.getTime(), this.endDate.getTime())
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "DatePickerChange Error: Unknown", false) }
	}

	/**
	 * rebuildURL
	 * Used to update the URL with the current duration and date range for the chart, it takes in the duration as a string and optional start and end dates, if the start and end dates are not provided it defaults to using the current values of paramStartDate and paramEndDate, it constructs a query parameter object with the duration, from, and to values and uses the router to navigate to the same route with the updated query parameters, this allows for bookmarking or sharing of specific views of the chart based on the selected time range
	 * @param duration Duration string in the format of "DD:HH:MM"
	 * @param start Start Date
	 * @param end End Date
	 * @param sensors Array of sensors
	 */
	rebuildURL(duration: string, start: any = this.paramStartDate.getTime(), end: any = this.paramEndDate.getTime()): void {
		let queryParams = {
			'duration': duration,
			'from': start,
			'to': end,
		}
		this.router.navigate([], { queryParams: queryParams, queryParamsHandling: 'merge' });
	}

	/**
	 * selectTime
	 * Used to handle the selection of a time duration for displaying sensor data on the chart, it takes a string input representing the duration in the format of "DD:HH:MM" and calculates the corresponding start and end dates based on the current date and time, it then updates the visible start and end dates for the chart and calls the setXAxis method to adjust the X-axis configuration accordingly, finally it rebuilds the URL with the new duration and date range to allow for bookmarking or sharing of specific views of the chart
	 * @param sDuration string in the format of "DD:HH:MM" representing the duration to display on the chart
	 */
	selectTime(sDuration: string) {
		this.strDuration = sDuration

		let parts: any[] = this.strDuration.split(":")
		this.endDate = new Date()
		this.startDate = new Date()
		this.startDate.setDate(this.startDate.getDate() - parts[0]);
		this.startDate.setHours(this.startDate.getHours() - parts[1]);
		this.startDate.setMinutes(this.startDate.getMinutes() - parts[2]);

		this.visibleStartDate = this.startDate;
		this.visibleEndDate = this.endDate;

		this.setXAxis();

		this.rebuildURL(this.strDuration, this.startDate.getTime(), this.endDate.getTime())
	}

	/**
	 * setXAxis
	 * Used to set the configuration for the X-axis of the sensor chart, it determines the appropriate interval type and label format based on the selected duration of data being displayed on the chart, it also configures the appearance of the axis labels and grid lines to ensure that the chart is displayed in a clear and readable format regardless of the time range being viewed
	 * the method takes an optional parameter of strip lines which can be used to add additional lines to the chart for highlighting specific time periods or events, it is designed to be flexible and adaptable to different time ranges and data sets to provide an optimal viewing experience for the user
	 * @param lines stripLine Definition
	 */
	setXAxis(lines: object[] = []): void {

		/* Sensor Graph specific variables */
		this.sensorXAxis = {
			labelIntersectAction: "Wrap",
			valueType: "DateTime", //'Category',
			intervalType: parseInt(this.strDuration.split(":")[0]) > 2
				|| (this.strDuration == 'custom' && (this.endDate.getTime() - this.startDate.getTime() >= (3 * 24 * 60 * 60 * 1000)))
				? "Days" : "Hours",
			labelFormat: parseInt(this.strDuration.split(":")[0]) > 2
				|| (this.strDuration == 'custom' && (this.endDate.getTime() - this.startDate.getTime() >= (3 * 24 * 60 * 60 * 1000)))
				? "dd-MMM" : "HH:mm  dd-MMM",
			labelRotation: Browser.isDevice ? -45 : 0,
			majorTickLines: { visible: false },
			minorTickLines: { visible: false },
			majorGridLines: { visible: false },
			minorGridLines: { visible: false },
		};
	}

	/**
	 * load
	 * Event handler for when the chart is loaded, used to set the zoom state of the chart to true and trigger a refresh of the chart to ensure it is displayed correctly after loading, it can be used to handle any necessary adjustments to the chart layout or data when the chart is first loaded
	 * @param args Chart load Event
	 */
	public load(args: ILoadedEventArgs): void {
		// try { args.chart.zoomModule.isZoomed = true; }
		// catch (error) {this.globalFunctions.HandleError(error, "", false) }
		// try{args.chart.refresh();}
		// catch{}
		//console.log(args)
	}

	/**
	 * chartresized
	 * Event handler for when the chart is resized, used to trigger a refresh of the chart to ensure it is displayed correctly after resizing, it can be used to handle any necessary adjustments to the chart layout or data when the size of the chart changes
	 * @param event Chart resized event arguments
	 */
	chartresized(event: any) {
		// console.log(event);
	}

	private isZooming: boolean

	/**
	 * zoomComplete
	 * Event handler for when a zoom action is completed on the chart, used to maintain the scroll position of the chart container
	 * to prevent the user from losing their place on the chart when zooming in or out, it sets a flag to indicate that a zoom action is in progress and uses a timeout to reset the flag and restore the scroll position after the zoom action is completed
	 * @param args ISelectionCompleteEventArgs from the chart
	 */
	zoomComplete(args?: ISelectionCompleteEventArgs) {

		if (!this.isZooming) {
			this.isZooming = true;
			// console.log('Zooming completed', args);
			// Perform additional actions here

			// Reset the flag after some delay
			setTimeout(() => {
				this.isZooming = false;
				const scrollableDiv = document.getElementById('dockContent');

				// Restore the scroll position of the overflow div
				if (scrollableDiv) {
					scrollableDiv.scrollLeft = this.scrollPosition[0];
					scrollableDiv.scrollTop = this.scrollPosition[1];
				}
			}, 0); // Adjust the delay as needed
		}
	}

	/**
	 * chartMouseDown
	 * Event handler for when the mouse is clicked down on the chart, 
	 * used to capture the current scroll position of the chart container 
	 * so that it can be restored after zooming or dragging to prevent the user 
	 * from losing their place on the chart when interacting with it
	 * @param args Mouse down event arguments
	 */
	chartMouseDown(args: any) {
		// console.log("chartMouseDown")
		const scrollableDiv = document.getElementById('dockContent');
		const scrollX = scrollableDiv?.scrollLeft || 0;
		const scrollY = scrollableDiv?.scrollTop || 0;
		this.scrollPosition = [scrollX, scrollY]
	}

	/**
	 * dragComplete
	 * Event handler for when a drag selection is completed on the chart, 
	 * used to update the date range of the chart to the selected area and rebuild the URL 
	 * with the new date range to trigger a refresh of the data based on the new date range
	 * @param args IDragCompleteEventArgs from the chart
	 */
	public dragComplete(args: IDragCompleteEventArgs): void {

		// console.log("Drag Complete")

		// try {
		//     // Get range of the 1st Pen (All ranges are the same)
		//     let argData = args.selectedDataValues[0]

		//     /** Check some data is selected */
		//     if (argData.length > 0) {
		//         /** Read the start date */
		//         let startX: Date = new Date(argData[(argData.length - 1)].x)
		//         let endX: Date = new Date(argData[0].x)
		//         /** Add some time onto last end date to ensure it is captured in data */
		//         endX.setMinutes(endX.getMinutes() + 4)
		//         this.rebuildURL(this.SelectedSN, startX.getTime(), endX.getTime())
		//     }

		//     /** Reset Duration Search */
		//     this.chartDuration = ""
		// }
		// catch (error) { this.globalFunctions.HandleError(error, "dragComplete Error: Unknown", false) }

	}

	/**
	 * displaySpinner
	 * Show the Spinner
	 * @param show
	 */
	displaySpinner(show: boolean) {
		// this.globalFunctions.displaySpinner(show);
		this.spinner = show;
	}

	/**
	 * ShowPDFSpinner
	 * Show the PDF Spinner on the button
	 * @param show true | false
	 */
	ShowPDFSpinner(show: boolean): void {
		this.generatingPDF = show

		if (show == true) {
			/** Notify user PDF is being Created */
			this.globalFunctions.DisplaySnackBar({ message: "Creating PDF Document", action: "" })
		}
	}

	/**
	 * ShowExcelSpinner
	 * Show the Excel Spinner on the button
	 * @param show true | false
	 */
	ShowExcelSpinner(show: boolean): void {
		this.generatingExcel = show

		if (show == true) {
			/** Notify user Excel is being Created */
			this.globalFunctions.DisplaySnackBar({ message: "Creating Excel Document", action: "" })
		}
	}

	/**
	 * GetOrganisationAssets
	 * Get the assets for the selected organisation and filter to the selected asset, 
	 * also handles getting new assets if required when changing to a different asset without changing pages
	 * @param assetID
	 * @param dateStart
	 * @param dateEnd
	 */
	GetOrganisationAssets(assetID: any, dateStart: any, dateEnd: any) {
		if (!this.selectedAsset || this.selectedAsset.AssetID != assetID) {
		} else {
			this.getDPVs(dateStart, dateEnd);
		}
	}

	/**
	 * getDPVs
	 * Get the datapoint values for the selected asset and date range, 
	 * also handles getting new datapoints if required and maintaining scroll position of the chart 
	 * when refreshing data so user doesnt lose their place when changing date range or other filters
	 * @param dateStart
	 * @param dateEnd
	 */
	async getDPVs(dateStart: any, dateEnd: any) {
		if (this.myChart) {
			this.displaySpinner(true);

			this.selectedExportFilename = this.selectedAsset.Name;

			//this.myChart.series = [];
			//this.myChart.clearSeries()

			if (this.getNewDps) {
				this.dataPoints = [];
				this.dataPointIDs = []
				await this.GetDataPoints(this.selectedAsset);
			}

			// const scrollableDiv = document.getElementById('app-sidenav-content');
			const scrollableDiv = document.getElementById('dockContent');
			const scrollX = scrollableDiv?.scrollLeft || 0;
			const scrollY = scrollableDiv?.scrollTop || 0;

			await this.GetDataPointValues(this.selectedAsset, dateStart, dateEnd);

			// Restore the scroll position of the overflow div
			if (scrollableDiv) {
				scrollableDiv.scrollLeft = scrollX;
				scrollableDiv.scrollTop = scrollY;
			}
		} else {
			this.tabulate = false;
			setTimeout(() => {
				this.getDPVs(dateStart, dateEnd);
			}, 10);
		}
	}

	/**
	 * stopPropagation
	 * Stop the propagation of an event, used to prevent clicks on the chart from triggering other click events on the page such as opening the sidenav when clicking on a series in the chart to open the datapoint selector
	 * @param event
	 */
	stopPropagation(event: Event) {
		event.stopPropagation();
	}

	/**
	 * onClick
	 * Export the chart based on the selected export type and file name, uses the built in export functionality of the chart
	 * @param e
	 */
	onClick(e: Event): void {
		let fileName: string = (<HTMLInputElement>(document.getElementById('fileName'))).value;
		//let exportType: string = (<HTMLInputElement>(document.getElementById('exporttype'))).value;
		this.myChart.exportModule.export(<ExportType>this.selectedExportType, fileName);

	}

	public noDatapoints: boolean = false;

	/**
	 * GetDataPoints
	 * Get datapoints for the selected asset and filter to those that should be shown on the chart, also add delta datapoints if required
	 * @param asset
	 * @returns
	 */
	async GetDataPoints(asset: LevelAsset): Promise<boolean> {

		/** Return a Boolean Promise */
		return new Promise<boolean>((resolve, reject) => {
			this.AssetsDataService.getAssetDataPoints$(this.selectedAccountUID, this.selectedOrganisationUID, asset.AssetID, true).pipe(
				take(1), distinctUntilChanged(),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					if (this.checkFeature(AccountFeatures.Virtual_Sensors)) this.getVirtualSensors(asset);
				})
			).subscribe({
				next: async (dataPoints: any) => {
					try {
						this.noDatapoints = false;
						this.dataPoints = dataPoints.filter((x: AssetDataPoint) => x.ShowOnChart);
						this.dataPoints.forEach(datapoint => {
							datapoint.SI = datapoint.SI.replace('%', '');
							if (datapoint.IsCumulative && datapoint.ShowDeltaValue) {
								this.includesCumulativeDatapoints = true;
								if (datapoint.SI != "kWh") {
									this.dataPoints.push({
										...datapoint,
										DataPointID: parseInt(datapoint.DataPointID.toString()) + "_DELTA",
										SI: datapoint.SI + " (Delta)",
										PVName: datapoint.PVName + " - Delta",
										DPTitle: datapoint.DPTitle + " - Delta",
										PrimaryIndex: false,
										IsCumulative: false,
										ShowDeltaValue: false
									});
								} else {
									this.dataPoints.push({
										...datapoint,
										DataPointID: parseInt(datapoint.DataPointID.toString()) + "_DELTA",
										SI: datapoint.SI + " (Usage)",
										PVName: datapoint.PVName + " - Usage",
										DPTitle: datapoint.DPTitle + " - Usage",
										PrimaryIndex: false,
										IsCumulative: false,
										ShowDeltaValue: false
									});
									this.shouldAddSecondXAxis = true;
								}
							}
						});
						this.dataPointIDs = this.dataPoints.map((x: DataPoint) => x.DataPointID.toString());
						this.selectedDatapoints = this.dataPoints;
						this.enabledDatapoints = this.dataPoints.map((x: AssetDataPoint) => ({ Datapoint: x, Enabled: true }));
						resolve(true);
					} catch (error) {
						this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints.next", false);
						reject(true);
					}
				},
				error: (error) => {
					this.noDatapoints = true;
					this.displaySpinner(false);
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints$", false);
					}
					reject(true);
				},
				//complete: () => { console.log("this.AssetsDataService.getAssetDataPoints$.subscribe: Complete"); }
			});
		});
	}

	/**
	 * RefreshMyChart
	 * Set the chart to redraw and refresh to update the series with new data without rebuilding the entire chart, 
	 * used after changing series data to ensure changes are reflected on the chart
	 */
	RefreshMyChart() {
		this.myChart.redraw = true;
		this.myChart.refresh();
	}

	/**
	 * openSidenav
	 * Open the sidenav with the selected tab, if the same tab is selected toggle the sidenav
	 */
	openSidenav(tab: number) {
		try {
			if (this.selectedItem != tab) {
				this.selectedItem = tab;
				this.dpSidenav.open();
				this.RefreshMyChart()
			} else {
				this.dpSidenav.toggle();
			}
		} catch (error) { console.info(error); }
	}

	/**
	 * closeSidenav
	 * Close the sidenav if it is open
	 */
	closeSidenav(matSN: MatSidenav) {
		try {
			if (matSN.opened) { matSN.close(); }
		} catch (error) { console.info(error); }
	}

	/**
	 * ManageRules
	 * Open Rule List as a pop up dialog and manage rules for the selected asset based on the result
	 */
	ManageRules() {
		let data: RuleListDialogData = {
			asset: this.selectedAsset,
			isAdmin: this.thisUser.AdminSite || this.selectedAccount.Admin,
			currentUser: this.thisUser,
			accountGUID: this.selectedAccountUID,
			organisationUID: this.selectedOrganisationUID,
		}

		const dialogRef = this.dialog.open(RuleListComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
			// filter(result => !!result)
		).subscribe(result => {
			this.getRules(this.selectedAsset);
			this.siteSubscriptions.RefreshChart(true);
		});
	}

	/** ManageDatapoints
	 *  Open datapoints list as a pop up dialog
	 */
	ManageDatapoints(): void {
		let data: EditAssetSettingsDialogData = {
			assetItem: this.selectedAsset,
			selectedAccountUID: this.selectedAccountUID,
			OrganisationUID: this.selectedOrganisationUID,
			isAdmin: this.thisUser.AdminSite || this.selectedAccount.Admin
		}

		const dialogRef = this.dialog.open(EditAssetDatapointsComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
			// filter(result => !!result)
		).subscribe(result => {
			this.GetDataPoints(this.selectedAsset);
			this.siteSubscriptions.RefreshChart(true);
		});
	}

	/**
	 * createAction
	 * Open Create Action as a pop up dialog and create an action based on the result
	 */
	createAction() {
		let dialogData = <createActionDialogData>{
			assetItem: this.selectedAsset,
			isAttachedtoEvent: false
		}

		const dialogRef = this.dialog.open(CreateActionComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe(result => {
			if (!result) {
				// this.createAction();
			} else {
				if (!result.skipAction) {
					let newAction: Action = {
						ActionID: 0,
						ActionType: result.actionType,
						AssetID: this.selectedAsset.AssetID,
						AssetName: `${this.selectedAsset.Name} - ${this.selectedAsset.Description}`,
						EventID: 0,
						TimeLineID: 0,
						Timestamp: 0,
						Completed: false,
						Details: result.details,
						CompletedTimestamp: 0,
						CompletedByUserID: '',
						CreatedByUserSub: '',
						AssignedToUserSub: result.assignedToUser,
						WorkOrderReference: result.workOrderReference,
						TargetCompletionTimestamp: result.targetCompletionTimestamp.getTime(),
						DisabledRules: result.ruleList ? result.ruleList.toString() : ''
					}
					let disable;
					if (result.ruleList && result.ruleList.length > 0) {
						disable = true;
					}

					this.actionsDataService.postAction$(this.selectedAccountUID, this.selectedOrganisationUID, newAction, disable).pipe(
						take(1), distinctUntilChanged(),
						filter((action) => !!action)
					).subscribe({
						next: (action) => {
							this.globalFunctions.DisplaySnackBar({
								message: "Action Created",
								type: SnackType.Info,
								duration: 1000
							});
						}
					});
				}
			}
		});
	}

	/**
	 * axisLabel
	 * Customise the xAxis label to show date and time on separate lines
	 * @param args IAxisLabelRenderEventArgs from the chart
	 */
	public axisLabel(args: IAxisLabelRenderEventArgs) {
		const date = new Date(args.value);
		args.text = `${date.toLocaleDateString()}\r\n${date.toLocaleTimeString()}`;
	}

	/**
	 * openAssetModels
	 * Open Asset Models as a pop up dialog
	 */
	openAssetModels() {
		let data = {
			asset: this.selectedAsset
		}

		const dialogRef = this.dialog.open(AssetModelsComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});
	}

	/**
	 * compareAsset
	 * Navigate to the compare page with the selected asset as a query param
	 */
	compareAsset() {
		this.router.navigate([`${this.selectedAccountUID}/organisation/${this.selectedOrganisationUID}/compare`],
			{ queryParams: { asset1: this.selectedAsset.AssetID } }
		);
	}

	/** popUpWatchList
	 *  Open watch list as a pop up dialog
	 */
	popUpWatchList(): void {
		const dialogRef = this.dialog.open(WatchListComponent);

		dialogRef.afterClosed().pipe(filter(result => !!result)).subscribe(result => { });
	}

	/**
	 * popUpHistory 
	 * functionality based on issue #4
	 */
	popUpHistory(): void {
		let data = {
			asset: this.selectedAsset,
			accountGUID: this.selectedAccountUID,
			organisationUID: this.selectedOrganisationUID,
		}

		const dialogRef = this.dialog.open(AssetHistoryComponent, { data: data });

		dialogRef.afterClosed().pipe(filter(result => !!result)).subscribe(result => { });
	}

	/**
	 * popUpInformation
	 * Open Asset Information as a pop up dialog
	 */
	popUpInformation(): void {
		let data: AssetInformationDialogData = {
			asset: this.selectedAsset,
			accountGUID: this.selectedAccountUID,
			organisationUID: this.selectedOrganisationUID,
			account: this.selectedAccount
		}

		const dialogRef = this.dialog.open(AssetInformationComponent, { data: data });

		dialogRef.afterClosed().pipe(filter(result => !!result)).subscribe(result => { });
	}

	/**
	 * popUpActionList
	 * Open Action List as a pop up dialog
	 */
	popUpActionList(): void {
		let data: ActionListDialogData = {
			asset: this.selectedAsset,
			accountGUID: this.selectedAccountUID,
			organisationUID: this.selectedOrganisationUID,
		}

		if (this.thisUser) { data.currentUser = this.thisUser; }

		const dialogRef = this.dialog.open(AssetActionsComponent, { data: data });

		dialogRef.afterClosed().pipe(filter(result => !!result)).subscribe(result => { });
	}

	/**
	 * GetDataPointValues
	 * Get the Data Point Values for the Asset and Date Range
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	async GetDataPointValues(asset: LevelAsset, dateStart: any, dateEnd: any): Promise<boolean> {

		this.displaySpinner(true);

		this.stopCheckingAvg = true;
		this.dataChartMaximums = [];
		this.barData = [];

		const scrollableDiv = document.getElementById('dockContent');
		const scrollX = scrollableDiv?.scrollLeft || 0;
		const scrollY = scrollableDiv?.scrollTop || 0;

		return new Promise<boolean>((resolve, reject) => {
			this.AssetsDataService.getAssetDataPointValues$(this.selectedAccountUID, this.selectedOrganisationUID, asset.AssetID, dateStart, dateEnd).pipe(
				take(1), distinctUntilChanged(),
				// filter((dataPointValues) => !!dataPointValues),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					this.displaySpinner(false);
					this.enableChartRefresh();
					if (this.checkFeature(AccountFeatures.Virtual_Sensors) && this.assetVirtualSensors.length > 0) {
						this.getVirtualSensorValues();
					}
					if (scrollableDiv) {
						scrollableDiv.scrollLeft = scrollX;
						scrollableDiv.scrollTop = scrollY;
					}
				})
			).subscribe({
				next: (dataPointValues: any) => {
					this.myChart.primaryXAxis.stripLines = [];
					if (!dataPointValues) {
						this.globalFunctions.DisplaySnackBar({
							message: "Something went wrong with that request, please try again later.",
							duration: 3000,
							action: ""
						});
						return;
					}
					try {
						let parsedData: ISensorData[];
						parsedData = dataPointValues.map((dataPointValue: any) => {
							return {
								//AssetId: dataPointValue.AssetID,
								AssetId: asset.AssetID,
								timestamp: dataPointValue.Timestamp,
								sampleDate: new Date(dataPointValue.Timestamp),
								...dataPointValue.Values,
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaValues).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA" + "]"]: value };
									})
								),
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaTimestamps).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA_TIME" + "]"]: value };
									})
								)
							}
						});

						parsedData.forEach((x: ISensorData) => {
							const DPID = parseInt(Object.entries(x).find((y) => this.regex.test(y[0]))?.[0].replace("[", "").replace("]", "") ?? "");
							if (DPID) {
								if (this.dataPoints && this.dataPoints.some((dp: AssetDataPoint) => dp.IsCumulative && dp.SI == "kWh" && dp.DataPointID == DPID)) {
									x[`[${DPID}_DELTA_CALCULATED]`] = (<number>this.getUsageValue(x, DPID)) ?? 0;
								}
							}
						});

						this.parsedData = parsedData;
						this.formatGridData();

						// Extract unique pairs of Si and Pv
						let uniqueDPPairs = [...new Set(this.dataPoints.map((x: DataPoint) => `${x.DataPointID},${x.SI},${x.EngMin},${x.EngMax}`))];
						// console.log(this.dataPoints)
						let uniqueSIPairs = [
							...new Set(
								this.dataPoints
									.sort((a: DataPoint, b: DataPoint) => {
										const aPrimaryIndex = a.PrimaryIndex ? "true" : "false";
										const bPrimaryIndex = b.PrimaryIndex ? "true" : "false";
										return aPrimaryIndex.localeCompare(bPrimaryIndex) * -1;
									})
									.map((x: DataPoint) => `${x.SI},${x.PrimaryIndex}`)
							)
						];

						// parse the unique pairs back into separate Datapoints
						let uniqueDP = uniqueDPPairs.map((pair: any) => {
							let [dp, si, EngMin, EngMax] = pair.split(",");
							return { Dp: dp, Si: si, Max: EngMax, Min: EngMin };
						});

						this.uniqueDPs = uniqueDP;

						this.averagedData = this.averageSensorData(parsedData);

						// Calculate the Min and Max from the Actual Data based on the Datapoint						
						let calcMinMax = uniqueDP.map((DP: any, index) => {
							const values = parsedData
								.map(item => item[`[${DP.Dp}]`])
								.filter(value => typeof value === 'number') as number[];
							// Find the minimum value
							if (values.length != 0) {
								DP.Min = values.reduce((min, val) => val < min ? val : min, values[0]);
								DP.Max = values.reduce((max, val) => val > max ? val : max, values[0]);
							} else {
								DP.Min = 0;
								DP.Max = 0;
							}
						});

						// parse the unique pairs back into separate Si and Pv values
						let uniqueSI = uniqueSIPairs.map((pair: any) => {
							let [si, primary] = pair.split(",");
							return { Si: si, Primary: primary };
						});

						// Create axes for each unique unit
						let siAxes = uniqueSI.map((unit: any, index) => {

							// Calculate the Min and Max from the SI data in the chart
							let { min, max, interval } = this.findMinMaxForSi(uniqueDP, unit.Si);

							/** This might have originally been for the lineRun signal */
							if (min === max) {
								if (min === 1) { min = 0; max = 1.5 }
								else if (min === 0) { max = 1; }
								else {
									const padding = Math.abs(min * 0.1) || 1;
									min -= padding;
									max += padding;
								}
							}

							// Build Axis
							let axis: any = {
								// Assuming you want each axis to have a unique name based on the unit
								name: `Axis${unit.Si}`,
								// You can add more properties here if needed

								majorTickLines: { width: 0 },
								minorTickLines: { width: 0 },

								majorGridLines: { width: 0 },
								minorGridLines: { width: 0 },

								minimum: min,
								maximum: max, /** Let the Chart calculate the Max */

								interval: interval,//0.2,
								rowIndex: 1,
								lineStyle: { width: 1 },
								labelFormat: `{value} ${unit.Si}`,
							};

							// Set opposedPosition to true only for the none primary Value
							if (unit.Primary != 'true') {
								axis.opposedPosition = true;
								axis.rowIndex = 0;
							}
							return axis;
						});

						// this.myChart.primaryYAxis.visible=false;
						siAxes[0].majorGridLines = { width: 1 };
						this.myChart.primaryYAxis = siAxes[0];

						//this.myChart.primaryXAxis.majorGridLines = { width: 0 };

						// Creating an array of the remaining elements of 'axes'
						let remainingAxes = siAxes.slice(1);
						// Assigning the remaining axes to the 'axes' property of 'myChart'
						this.myChart.axes = remainingAxes;

						this.addSecondXAxis();

						// Clear previous series
						this.myChart.clearSeries()
						// this.seriesColors = [];
						// this.defaultColorIndex = 0;

						// return;
						// Add new Series
						this.dataPoints.forEach((dPoint: AssetDataPoint) => {
							if (dPoint.ShowOnChart && this.selectedDatapoints.includes(dPoint)) {
								let seriesColor = 'red';
								let existingColor: boolean = false;
								let findExistingColor = this.seriesColors.find((x) => x.datapointID == dPoint.DataPointID);
								if (findExistingColor && findExistingColor.color) {
									seriesColor = findExistingColor.color;
									existingColor = true;
								}
								if (!existingColor) {
									if (dPoint.SI == 'g') {
										// Check if lightGreenCyan has already been used
										if (this.seriesColors.some(s => s.color === this.lightGreenCyan)) {
											// If used, choose a darker shade
											if (this.seriesColors.some(s => s.color === this.lightGreenCyanDarker1)) {
												if (this.seriesColors.some(s => s.color === this.lightGreenCyanDarker2)) {
													seriesColor = this.defaultColors[this.defaultColorIndex];
													this.defaultColorIndex = (this.defaultColorIndex + 1) % this.defaultColors.length;
												} else {
													seriesColor = this.lightGreenCyanDarker2;
												}
											} else {
												seriesColor = this.lightGreenCyanDarker1;
											}
										} else {
											seriesColor = this.lightGreenCyan;
										}

									} else if (dPoint.SI.includes('C')) {
										// Check if darkBlue has already been used
										if (this.seriesColors.some(s => s.color === this.darkBlue)) {
											// If used, choose a darker shade
											if (this.seriesColors.some(s => s.color === this.darkBlueDarker1)) {
												if (this.seriesColors.some(s => s.color === this.darkBlueDarker2)) {
													seriesColor = this.defaultColors[this.defaultColorIndex];
													this.defaultColorIndex = (this.defaultColorIndex + 1) % this.defaultColors.length;
												} else {
													seriesColor = this.darkBlueDarker2;
												}
											} else {
												seriesColor = this.darkBlueDarker1;
											}
										} else {
											seriesColor = this.darkBlue;
										}

									} else {
										// If no specific condition is met, use the next color in the default array
										seriesColor = this.defaultColors[this.defaultColorIndex];

										// Increment the defaultColorIndex, and loop back to 0 if we reach the end of the array
										this.defaultColorIndex = (this.defaultColorIndex + 1) % this.defaultColors.length;
									}
								}

								// console.log("Dynamic keys for this object:", dPoint.DataPointID);
								let width: number = 1;

								const SEVEN_DAYS: number = (7 * 24 * 60 * 60 * 1000);

								if (this.strDuration == "custom") {
									let difference: number = this.endDate.getTime() - this.startDate.getTime();
									if (difference >= SEVEN_DAYS) {
										width = 0.75;
									}
								} else {
									if (this.timeSpanToMilliseconds(this.strDuration) >= SEVEN_DAYS) {
										width = 0.75;
									}
								}

								let seriesName = dPoint.SI == '' ? dPoint.DPTitle : dPoint.DPTitle.includes(`(${dPoint.SI})`) ? dPoint.DPTitle : `${dPoint.DPTitle} (${dPoint.SI})`;
								let isDelta: boolean = (typeof (dPoint.DataPointID) == 'string' && dPoint.DataPointID.toLowerCase().includes("_delta"));
								let barData: any[] = [];
								let useBarData: boolean = false;
								if (isDelta && dPoint.SI == "kWh (Usage)") {
									const data = this.parsedData.map((x: ISensorData) => <number>x[`[${dPoint.DataPointID}_CALCULATED]`]);
									this.calculatedData.push({
										datapointID: dPoint.DataPointID,
										max: Math.max(...data),
										min: Math.min(...data)
									});
									const label: string = `[${dPoint.DataPointID}]`;
									barData = this.calculateDeltaChartBarData(parsedData, label);
									if (barData.length > 0) {
										const numArray = barData.map((x: ISensorData) => <number>x[label]);
										const max = Math.max(...numArray);
										const min = Math.min(...numArray);
										this.barData.push({ datapointID: dPoint.DataPointID, data: barData, max: max, min: min });
										useBarData = true;
										let findAxis = this.myChart.axes.find((x: AxisModel) => x.name == `Axis${dPoint.SI}`);
										if (findAxis) {
											const numArr = barData.map((x: ISensorData) => <number>x[label]);
											const max = Math.max(...numArr);
											const min = Math.min(...numArr);
											const interval = (max - min) / 10;
											findAxis.interval = interval;
											findAxis.maximum = max + interval;
											// findAxis.minimum = min;
										}
									}
								}

								if (!useBarData) {
									let newSeries = this.myChart.addSeries([{
										animation: {
											enable: false
										},
										emptyPointSettings: { mode: "Drop" },
										type: "Line",
										dataSource: parsedData,
										name: seriesName,
										xName: 'timestamp',
										yName: `[${dPoint.DataPointID}]`,
										marker: {
											visible: false,
											width: width,
											height: 8,
											shape: 'Triangle',
											dataLabel: { "name": 'x' },
											isFilled: true
										},
										yAxisName: `Axis${dPoint.SI}`,
										// xAxisName: "primaryXAxis",
										fill: seriesColor,
										width: width
									}]);
								}

								if (useBarData) {
									if (this.showDeltaAsBar) {
										let newSeries = this.myChart.addSeries([{
											animation: {
												enable: false
											},
											emptyPointSettings: { mode: "Drop" },
											type: "Column",
											dataSource: barData,
											name: seriesName,
											xName: 'timestamp',
											yName: `[${dPoint.DataPointID}]`,
											marker: {
												visible: false,
												width: width,
												height: 8,
												shape: 'Triangle',
												dataLabel: { "name": 'x' },
												isFilled: true
											},
											yAxisName: `Axis${dPoint.SI}`,
											xAxisName: "secondaryXAxis",
											fill: seriesColor,
											width: width,
											columnWidth: 1,
											columnSpacing: 0.2,
											zOrder: !isDelta ? 1 : 0,
											opacity: isDelta ? 0.75 : 1
										}]);
									} else {
										let newSeries = this.myChart.addSeries([{
											animation: {
												enable: false
											},
											emptyPointSettings: { mode: "Drop" },
											type: "Line",
											dataSource: parsedData,
											name: `${seriesName}`,
											xName: 'timestamp',
											yName: `[${dPoint.DataPointID}_CALCULATED]`,
											marker: {
												visible: false,
												width: width,
												height: 8,
												shape: 'Triangle',
												dataLabel: { "name": 'x' },
												isFilled: true
											},
											yAxisName: `Axis${dPoint.SI}`,
											// xAxisName: "primaryXAxis",
											fill: seriesColor,
											width: width
										}]);
									}
								}

								/** Load the datapoint with the new Colour */
								dPoint.SeriesColor = seriesColor;

								/** Check that the colour is not already in the list */
								if (!this.seriesColors.some(s => s.color === seriesColor)) {
									this.seriesColors.push({
										datapointID: dPoint.DataPointID,
										color: seriesColor
									})
								}
								dPoint.SeriesID = this.myChart.series.length - 1;
							}
						});

						if (this.myChart.primaryYAxis) {
							this.dataChartMaximums.push({
								axis: 0,
								maximum: this.myChart.primaryYAxis.maximum ?? 1,
								interval: this.myChart.primaryYAxis.interval ?? 1
							});
						}

						if (this.myChart.axes && this.myChart.axes.length > 0) {
							this.myChart.axes.forEach((axis, index) => {
								if (axis.maximum) {
									this.dataChartMaximums.push({
										axis: index + 1,
										maximum: axis.maximum,
										interval: axis.interval
									});
								}
							});
						}

						this.formatGridData();
						this.stopCheckingAvg = false;
						resolve(true);

					} catch (error) {
						console.error(error);
						reject(true);
					}
				},
				error: (error) => {
					this.dataPointValues = [];
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPointValues$", false)
					reject(true);
				},
				//complete: () => { console.log("this.AssetsDataService.getAssetDataPointValues$.subscribe: Complete"); }
			});
		});
	}

	/**
	 * findMinMaxForSi
	 * This function takes an array of data and a target SI unit as input and returns an object containing the minimum, maximum, and interval values for that SI unit. It filters the input data to include only objects where the SI matches the target SI, then extracts the Min and Max values from those objects to calculate the overall minimum and maximum. The interval is calculated based on the range between the minimum and maximum values, divided by 13. The function also includes logic to handle cases where the minimum and maximum are equal or when there are no matching objects found.
	 * @param data
	 * @param targetSi
	 * @returns
	 */
	findMinMaxForSi(data: any[], targetSi: string): { min: number | 0, max: number | 999, interval: number | 1 } {
		// Filter the array to include only objects where Si equals the targetSi
		const filtered = data.filter(item => item.Si.toLowerCase() === targetSi.toLowerCase());

		if (filtered.length === 0) {
			return { min: 0, max: 0, interval: 1 }; // No matching objects found
		}

		// Extract Min and Max values from the filtered objects
		const minValues = filtered.map(item => item.Min);
		const maxValues = filtered.map(item => item.Max);

		// Find the minimum and maximum values
		let min = Math.min(...minValues);
		let max = Math.max(...maxValues);

		min = Math.floor(min);
		max = Number(max.toFixed(1));
		let interval = ((max - min) / 13);
		if (interval > 1) {
			interval = Number(interval.toFixed(1));
			interval = Math.ceil(interval)
		} else { interval = this.roundToNearestTenth(interval); }

		max = max + interval;
		interval = (min == max) ? interval = 0.5 : interval

		if (min == 0 && max == 0 && Math.min(...minValues) != 0) {
			//min = Math.min(...minValues);
			max = Math.max(...maxValues);
			interval = 0.01;
		}
		return { min, max, interval };
	}

	/**
	 * roundToNearestTenth
	 * This function takes a number as input and rounds it up to the nearest tenth. It does this by multiplying the number by 10, using Math.ceil to round it up to the nearest whole number, and then dividing it back by 10 to get the rounded value. This is useful for ensuring that axis intervals on the chart are clean and easy to read.
	 * @param num
	 * @returns
	 */
	roundToNearestTenth(num: number): number {
		return Math.ceil(num * 10) / 10;
	}

	/**
	 * viewImage
	 * This function takes an image URL as input and uses a global function to display the image in full size. It is likely used to allow users to view images associated with datapoints or assets in a larger format for better visibility.
	 * @param imageURL
	 */
	viewImage(imageURL: string): void {
		this.globalFunctions.viewImageFullSize(imageURL);
		// this.numberRND = Math.random()
	}

	/**
	 * editDataPoint
	 * This function is responsible for handling the editing of a datapoint. It takes a DataPoint object as input, creates a local copy of it, and opens a dialog box for editing using the EditSensorDatapointComponent. After the dialog is closed, it checks if there is a result and if so, it updates the original DataPoint with the new values and sends a patch request to update the datapoint in the backend. It also handles the subscription lifecycle using a Subject to ensure that subscriptions are properly terminated after completion.
	 * @param editAsset
	 * @returns
	 */
	editDataPoint(editAsset: DataPoint): void {

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...editAsset };

		// Display the dialog box
		const dialogRef = this.dialog.open(EditSensorDatapointComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);
			this.DatapointsDataService.patchDataPoint$(this.selectedAccountUID, this.selectedOrganisationUID, _edit).pipe(
				take(1), distinctUntilChanged(),
				filter((response) => !!response)
			).subscribe({
				next: (response) => {
					Object.assign(editAsset, result);
					this.globalFunctions.DisplaySnackBar({
						message: "DataPoint Details Updated",
						type: SnackType.Info,
						duration: 1000
					});
					this.GetOrganisationAssets(this.paramAssetID, this.startDate.getTime(), this.endDate.getTime());
				},
				error: (error) => { this.globalFunctions.HandleError(error, "asset-thart Error: his.DatapointsDataService.patchDataPoint$", false) },
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});
	}

	/**
	 * getMinMaxForDP
	 * This function takes a datapointID as input and returns an array containing the minimum and maximum values for that datapoint. 
	 * It first checks if the provided datapointID exists in the list of dataPoints. If it does not exist, it returns an empty array. 
	 * If it exists, it retrieves the minimum and maximum values from the uniqueDPs array based on the matching datapointID. 
	 * The function also ensures that if the minimum or maximum values are strings, they are parsed into floats. Finally, it returns an array with the minimum and maximum values, formatted to three decimal places if they are not zero.
	 * @param datapointID
	 * @returns
	 */
	getMinMaxForDP(datapointID: number): number[] {
		let dpIDs: number[] = [];
		this.dataPoints.forEach(datapoint => {
			dpIDs.push(datapoint.DataPointID);
		})
		if (!dpIDs.includes(datapointID)) {
			return [];
		}

		let arr: number[] = [];
		let min: number | undefined = this.uniqueDPs.find((x: any) => x.Dp == datapointID)?.Min
		let max: number | undefined = this.uniqueDPs.find((x: any) => x.Dp == datapointID)?.Max
		if (typeof (min) == 'string') {
			min = parseFloat(min);
		}
		if (typeof (max) == 'string') {
			max = parseFloat(max);
		}
		if (min != null && max != null) {
			if (min != 0) {
				arr.push(parseFloat(min.toFixed(3)))
			} else {
				arr.push(min);
			}
			if (max != 0) {
				arr.push(parseFloat(max.toFixed(3)))
			} else {
				arr.push(max);
			}
		}
		return arr;
	}

	/**
	 * averageSensorData
	 * This function takes an array of sensor data objects as input and calculates the average and running average for each unique datapoint. 
	 * It iterates through the unique datapoints, sums up the values for each datapoint across all sensor data entries, and counts the number of values to compute the average.
	 * For the running average, it sums up values that are greater than 0.05 and counts them separately to compute the running average. Finally, it returns an array of objects containing the datapointID, average, and runningAvg for each unique datapoint.
	 * @param sensorData
	 * @returns
	 */
	averageSensorData(sensorData: ISensorData[]): { datapointID: number, average: number, runningAvg: number }[] {
		let result: { datapointID: number, average: number, runningAvg: number }[] = [];
		this.uniqueDPs.forEach(datapoint => {
			result.push({ datapointID: datapoint.Dp, average: 0, runningAvg: 0 });
			let numberOfValues: number = 0;
			let numRunningValues: number = 0;
			for (let i = 0; i < sensorData.length; i++) {
				if (sensorData[i][`[${datapoint.Dp}]`] != undefined) {
					let value: any = sensorData[i][`[${datapoint.Dp}]`] ?? 0
					result.forEach(item => {
						if (item.datapointID == datapoint.Dp) {
							item.average = item.average + value;
							numberOfValues++;
							if (value > 0.05) {
								item.runningAvg = item.runningAvg + value;
								numRunningValues++;
							}
						}
					})
				}
			}
			result.forEach(item => {
				if (item.datapointID == datapoint.Dp) {
					item.average = item.average / numberOfValues;
					item.runningAvg = item.runningAvg / numRunningValues;
					item.average = parseFloat(item.average.toFixed(3));
					item.runningAvg = parseFloat(item.runningAvg.toFixed(3));
				}
			})
		});
		return result;
	}

	/**
	 * findAverage 
	 * This function takes a DataPoint object as input and searches through the pre-calculated averaged data to find a matching entry based on the DataPointID. 
	   * If a match is found, it returns an object containing the datapointID, average, and runningAvg values; if no match is found, it returns an object with default values of 0 for average and runningAvg.
	 * @param dp
	 * @returns
	 */
	findAverage(dp: DataPoint): { datapointID: number, average: number, runningAvg: number } {
		let avg = this.averagedData.find((x: { datapointID: number, average: number, runningAvg: number }) => x.datapointID == dp.DataPointID);
		if (avg) {
			if (Number.isNaN(avg.runningAvg)) {
				avg.runningAvg = 0;
			}
			return avg;
		}
		return { datapointID: 0, average: 0, runningAvg: 0 };
	}

	/**
	 * getRules
	 * Fetches the rules for a given asset and updates the component's rule list.
	 * @param asset
	 */
	async getRules(asset: LevelAsset) {

		try {
			const AssetRules = await firstValueFrom(
				this.rulesDataService.getRules$(this.selectedAccountUID, this.selectedOrganisationUID, asset.AssetID).pipe(
					take(1),
					filter((rules) => !!rules)
				)
			);

			if (AssetRules) {
				this.assetRules = AssetRules;
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, "Error: rulesDataService.getRules$", false);
		}
	}

	/**
	 * makeRuleReadable
	 * This function takes a rule object as input and constructs a human-readable string representation of the rule by iterating through its conditions, 
	 * retrieving the corresponding datapoint information, and formatting the output to include the sensor name, unit, operator, limit values, and trigger time in a coherent manner.
	 * @param rule
	 * @returns
	 */
	makeRuleReadable(rule: Rule): string {
		let result: string = "";
		if (rule.RuleDef && this.dataPoints && this.dataPoints.length > 0) {
			for (let i: number = 0; i < rule.RuleDef?.length; i++) {
				let dp: any = this.getDatapointInAssetDataPoints(rule.RuleDef[i].DatapointID)
				if (dp) {
					if (rule.RuleDef[i].Operator != '~' && rule.RuleDef[i].Operator != '!~') {
						result += dp.SensorName + " " + dp.SI + rule.RuleDef[i].Operator + rule.RuleDef[i].Limit;
					} else {
						result += dp.SensorName + " " + dp.SI + " between " + rule.RuleDef[i].Limit + " and " + rule.RuleDef[i].LowerLimit;
					}
					if (i != rule.RuleDef.length - 1) {
						const comp = rule.RuleText.match(/AND|OR/)?.[0] || "";
						result += " " + comp + " ";
					}
				}
			}
			result += " for " + rule.TriggerTime + " minutes";
		}
		return result;
	}

	/**
	 * getDatapointInAssetDataPoints
	 * This function takes a DataPointID as input and searches through the list of datapoints associated with the asset to find a match. 
	 * If a datapoint with the specified DataPointID is found, it returns the full datapoint object; otherwise, it returns undefined.
	 * @param datapointID
	 * @returns
	 */
	getDatapointInAssetDataPoints(datapointID: number): DataPoint | undefined {
		return this.dataPoints.find((x: DataPoint) => x.DataPointID == datapointID)
	}

	/**
	 * ruleSelected
	 * When a user selects a rule, this function identifies the associated datapoints and their corresponding axes on the chart, 
	 * then adds strip lines to those axes at the specified limit values defined in the rule, using distinct colors for visual differentiation,
	 * while preserving the user's current scroll position on the chart to ensure a seamless user experience without unexpected shifts in view.
	 * @param rule
	 */
	ruleSelected(rule: Rule) {
		const scrollableDiv = document.getElementById('dockContent');
		const scrollX = scrollableDiv?.scrollLeft || 0;
		const scrollY = scrollableDiv?.scrollTop || 0;

		if (rule.RuleDef) {
			let dashArray: string = "10,5";
			// let colour1: string = 'orange';
			// let colour2: string = 'red';
			let dp = this.getDatapointInAssetDataPoints(rule.RuleDef[0].DatapointID);
			// if (dp) {
			// 	let temp = this.seriesColors.find((x: any) => x.datapointID == dp?.DataPointID);
			// 	if (temp) {
			// 		colour1 = temp.color;
			// 	}
			// }
			let dp2: DataPoint | undefined = new DataPoint;
			if (rule.RuleDef.length > 1) {
				dp2 = this.getDatapointInAssetDataPoints(rule.RuleDef[1].DatapointID);
				// if (dp2) {
				// 	let temp = this.seriesColors.find((x: any) => x.datapointID == dp2?.DataPointID);
				// 	if (temp) {
				// 		colour2 = temp.color;
				// 	}
				// }
			}

			let dps: any[] = [dp ? dp : undefined, dp2 ? dp2 : undefined];

			dps.forEach(datapoint => {
				if (datapoint && datapoint.DataPointID != 0) {
					let size;
					let existingStripLines: StripLineSettingsModel[] = [];

					let axes: AxisModel[] = [];
					if (this.myChart.primaryYAxis) {
						axes.push(this.myChart.primaryYAxis);
					}
					if (this.myChart.axes.length > 0) {
						axes.push(...this.myChart.axes);
					}

					let axis = axes.find((x: any) => x.series.find((y: any) => y.name.includes(datapoint.DPTitle)));

					let definition = rule.RuleDef!.find((x: RuleDefinition) => x.DatapointID == datapoint.DataPointID);

					if (axis && definition) {
						if (axis.stripLines) {
							existingStripLines = axis.stripLines;
						}

						if (<number>axis.maximum <= definition.Limit) {
							axis.maximum = definition.Limit + <number>axis.interval;
							let max: any = axis.maximum;
							let min: any = axis.minimum;
							let interval = (max - min) / 15;
							axis.interval = Math.round(interval) || interval;
						}

						if (<number>axis.minimum >= definition.Limit) {
							axis.minimum = definition.Limit - <number>axis.interval;
							let max: any = axis.maximum;
							let min: any = axis.minimum;
							let interval = (max - min) / 15;
							axis.interval = Math.round(interval) || interval;
						}

						if (axis.interval) {
							size = axis.interval / 10;
							axis.interval = parseFloat(axis.interval.toFixed(1));
						}

						// stopping striplines being too fat
						if (axis.maximum && size && (<number>axis.maximum <= (size * 40))) {
							size /= 5;
						}

						let color = this.seriesColors.find((x: any) => x.datapointID == datapoint?.DataPointID)?.color;
						existingStripLines.push(
							{
								startFromAxis: false,
								start: definition.Limit - (size! / 2),
								size: size,
								zIndex: 'Behind',
								opacity: 1,
								color: color ? color : 'orange',
								dashArray: dashArray
							}
						);
						axis.stripLines = existingStripLines;
					}
				}
			});
		}
		setTimeout(() => {
			if (scrollableDiv) {
				scrollableDiv.scrollLeft = scrollX;
				scrollableDiv.scrollTop = scrollY;
			}
		}, 0);
	}

	/**
	 * navigateByDuration
	 * allows users to navigate through data based on a specified duration, either moving forward or backward in time, 
	 * by calculating the appropriate start and end timestamps for data retrieval and updating the chart accordingly, 
	 * providing an intuitive way for users to explore data trends over different time periods
	 * @param isForward
	 * @returns
	 */
	navigateByDuration(isForward: boolean) {
		let duration: string = "";
		let start: number = 0;
		let end: number = 0;
		if (this.strDuration) {
			duration = this.strDuration;
		}

		if (duration == "custom") {
			let difference: number = this.endDate.getTime() - this.startDate.getTime();
			if (!isForward) {
				end = this.startDate.getTime();
				start = end - difference;
				this.startDate = new Date(Number(start));
				this.endDate = new Date(Number(end));
				this.GetDataPointValues(this.selectedAsset, start, end);
			} else {
				start = this.endDate.getTime();
				end = start + difference;
				if (end > Date.now()) {
					end = Date.now();
					start = end - difference;
				}
				this.startDate = new Date(Number(start));
				this.endDate = new Date(Number(end));
				this.GetDataPointValues(this.selectedAsset, start, end);
			}

		} else {
			let difference: number = this.timeSpanToMilliseconds(duration);
			if (!isForward) {
				end = this.startDate.getTime();
				start = end - difference;
				this.startDate = new Date(Number(start));
				this.endDate = new Date(Number(end));
				this.GetDataPointValues(this.selectedAsset, start, end);
			} else {
				start = this.endDate.getTime();
				end = start + difference;
				if (end > Date.now()) {
					end = Date.now();
					start = end - difference;
				}
				this.startDate = new Date(Number(start));
				this.endDate = new Date(Number(end));
				this.GetDataPointValues(this.selectedAsset, start, end);
			}
		}
		this.visibleStartDate = this.startDate;
		this.visibleEndDate = this.endDate;
	}

	/**
	 * timeSpanToMilliseconds
	 * converts a time span string in the format of "days:hours:minutes" into its equivalent value in milliseconds, 
	 * allowing for easy manipulation and calculation of time durations in milliseconds based on the provided time span string
	 * @param timeSpan
	 * @returns
	 */
	timeSpanToMilliseconds(timeSpan: string): number {
		// Split the string into components
		const [days, hours, minutes] = timeSpan.split(':').map(Number);

		// Validate the input
		if (isNaN(days) || isNaN(hours) || isNaN(minutes)) {
			throw new Error("Invalid time span format");
		}

		// Calculate the total milliseconds
		const milliseconds =
			(days * 24 * 60 * 60 * 1000) + // Days to milliseconds
			(hours * 60 * 60 * 1000) +     // Hours to milliseconds
			(minutes * 60 * 1000);         // Minutes to milliseconds

		return milliseconds;
	}

	/**
	 * getShortDate
	 * formats a given Date object into a short date string format, which includes the day of the month, 
	 * abbreviated month name, and time in hours and minutes, providing a concise representation of the date and time for display purposes
	 * @param date
	 * @returns
	 */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/** 
	 * selectRule
	 * allows users to select or deselect a specific rule, which triggers the retrieval and plotting of associated rule events on a chart, 
	 * while also managing the visual representation of the chart by clearing existing strip lines and adjusting axis parameters based on the selected rules, 
	 * providing an interactive way for users to analyze the impact of specific rules on the data displayed in the chart
	 * @param rule
	 * @param event 
	 */
	selectRule(rule: Rule, event: any) {
		if (event.checked) {
			this.selectedRules.push(rule);
		} else {
			this.selectedRules = this.selectedRules.filter((x: Rule) => x.RuleID != rule.RuleID);
		}

		const scrollableDiv = document.getElementById('dockContent');
		const scrollX = scrollableDiv?.scrollLeft || 0;
		const scrollY = scrollableDiv?.scrollTop || 0;

		this.clearChartAxis();
		this.selectedRules.forEach(rule => {
			this.getRuleEvents(rule);
		});

		if (this.selectedRules.length == 0) {
			if (this.myChart.primaryYAxis) {
				let info: { axis: number, maximum: any, interval: any } | undefined = this.dataChartMaximums.find((x) => x.axis == 0);
				if (info) {
					this.myChart.primaryYAxis.interval = info.interval;
					this.myChart.primaryYAxis.maximum = info.maximum + info.interval;
				}
			}
			if (this.myChart.axes && this.myChart.axes.length > 0) {
				this.myChart.axes.forEach((axis, index) => {
					let info: { axis: number, maximum: any, interval: any } | undefined = this.dataChartMaximums.find((x) => x.axis == index + 1);
					if (info) {
						axis.interval = info.interval;
						axis.maximum = info.maximum + info.interval;
					}
				});
			}
		}

		setTimeout(() => {
			if (scrollableDiv) {
				scrollableDiv.scrollLeft = scrollX;
				scrollableDiv.scrollTop = scrollY;
			}
		}, 0);
	}

	/**
	 * clearChartAxis
	 * clears all strip lines from the primary Y-axis, primary X-axis, and any additional axes in the chart, 
	 * effectively removing any visual indicators or highlights that were previously added to the chart's axes, 
	 * allowing for a clean slate before plotting new data or events on the chart
	 */
	clearChartAxis() {
		this.myChart.primaryYAxis.stripLines = [];
		this.myChart.primaryXAxis.stripLines = [];
		this.myChart.axes.forEach(axis => {
			axis.stripLines = [];
		});
	}

	/** 
	 * getRuleEvents
	 * retrieves events associated with a specific rule and plots them on a chart by adding strip lines to the primary X-axis, 
	 * where each strip line represents a rule event with its timestamp and trigger time, allowing users to visually analyze 
	 * the occurrence of rule events in relation to the data points on the chart for better insights and understanding of patterns or anomalies
	 * @param rule
	 */
	getRuleEvents(rule: Rule) {

		this.eventsDataService.getRuleEvents$(this.selectedAccountUID, this.selectedOrganisationUID, rule.RuleID).pipe(
			take(1), distinctUntilChanged(),
			filter(ruleEvents => !!ruleEvents),
			finalize(() => { this.ruleSelected(rule); })
		).subscribe({
			next: (ruleEvents: any) => {
				let events: RuleEvent[] = ruleEvents;
				this.plotRuleEvents(events, rule);
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getRuleEvents$", false); },
		});
	}

	/** 
	 * plotRuleEvents
	 * plots rule events on a chart by adding strip lines to the primary X-axis of the chart, 
	 * where each strip line represents a specific rule event with its timestamp and trigger time,
	 * allowing users to visually analyze the occurrence of rule events in relation to the data points 
	 * on the chart for better insights and understanding of patterns or anomalies
	 * @param ruleEvents
	 * @param rule
	 */
	plotRuleEvents(ruleEvents: RuleEvent[], rule: Rule) {
		let currentStriplines: StripLineSettingsModel[] = [];
		if (this.myChart.primaryXAxis.stripLines) {
			currentStriplines = this.myChart.primaryXAxis.stripLines;
		}

		ruleEvents.forEach(ruleEvent => {
			currentStriplines.push({
				startFromAxis: false,
				start: ruleEvent.TimeStamp,
				// size: 1,
				end: ruleEvent.TimeStamp + 1000,
				zIndex: 'Over',
				opacity: 1,
				color: 'red',
				border: { color: 'red', width: 2 }
			});
			currentStriplines.push({
				startFromAxis: false,
				start: ruleEvent.TimeStamp - (rule.TriggerTime * 60 * 1000),
				// size: 1,
				end: ruleEvent.TimeStamp,
				zIndex: 'Over',
				opacity: 0.25,
				border: { color: 'red', width: 1 },
				isSegmented: true, // Enable the segmented mode for stripline
				segmentAxisName: 'primaryXAxis', // Replace with the axis name if different
				text: ruleEvent.RuleText, // Add tooltip text
				rotation: -45,
				// verticalAlignment: 'Start',
				textStyle: { color: 'transparent', fontWeight: 'Bold' }
			});
		})

		this.myChart.primaryXAxis.stripLines = currentStriplines;
	}

	/**
	 * onTooltipRender
	 * handles the rendering of tooltips on a chart by formatting the timestamp associated with the data point being hovered over,
	 * converting it into a more human-readable format that includes the date and time, and then updating the tooltip text to display 
	 * this formatted timestamp along with the original tooltip content, allowing users to easily understand the context of the data point 
	 * they are examining
	 * @param args args
	 */
	onTooltipRender(args: any): void {
		let timestamp: Date = args.data.pointX;
		if (typeof (timestamp) == 'object') {
			const day = timestamp.getDate();
			const monthIndex = timestamp.getMonth();
			const minutes = timestamp.getMinutes();
			const hours = timestamp.getHours();
			const seconds = timestamp.getSeconds();
			const formattedDate = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${day} ${this.shortMonths[monthIndex]}`;
			args.text = `${formattedDate} :${args.text.split(":").pop()}`;
		}
	}

	/**
	 * onItemClicked
	 * handles the click event on a list item, retrieves the associated data index from the clicked element's attributes, 
	 * and logs the clicked item for further processing, such as highlighting a corresponding series on a chart or 
	 * performing other actions based on the user's selection, allowing for interactive and dynamic user interfaces 
	 * that respond to user input effectively
	 * @param evt
	 */
	onItemClicked(evt: any) {
		const li = evt.currentTarget as HTMLElement;
		const dataIndex = li.getAttribute('data-uid'); // or use index in your data source
		console.log('Clicked item:', dataIndex);

		//this.highlightSeries(dataIndex); // your function to highlight chart series
	}

	/**
	 * HighlightDP
	 * highlights a specific datapoint on the chart based on user interaction, 
	 * by finding the corresponding series in the chart for the selected datapoint and adjusting 
	 * the opacity of all other series to create a visual emphasis on the highlighted datapoint, 
	 * while also refreshing the chart to reflect the changes in opacity and allowing users to easily 
	 * identify and focus on specific data points of interest for analysis or comparison
	 * @param datapoint
	 */
	HighlightDP(datapoint: DataPoint) {

		let item = this.enabledDatapoints.find((x) => x.Datapoint.DataPointID == datapoint.DataPointID);
		if (item) {

			let series = this.myChart.series.find((x: SeriesModel) => x.yName == `[${item!.Datapoint.DataPointID}]`);

			if (series && this.HilightedSeries?.DataPointID != datapoint.DataPointID) {
				for (const seriesReset of this.myChart.series) {
					seriesReset.opacity = 0.5;
				}
				if (series.type == "Line") {
					series.opacity = 1;
				} else if (series.type === "StepArea") {
					series.opacity = 0.75;
				}
				this.HilightedSeries = datapoint;
				this.myChart.refresh();
				return;
			} else { this.HilightedSeries = undefined; }

			for (const seriesReset of this.myChart.series) {
				if (!this.HilightedSeries) {
					if (seriesReset.type == "Line") {
						seriesReset.opacity = 1;
					} else if (seriesReset.type === "StepArea") {
						seriesReset.opacity = 0.75;
					}
				} else if (series != seriesReset) {
					if (seriesReset.type == "Line") {
						seriesReset.opacity = 0.5;
					} else if (seriesReset.type === "StepArea") {
						seriesReset.opacity = 0.75;
					}
				}
			}
			this.myChart.refresh();
		}
	}

	/**
	 * toggleDatapoint
	 * toggles the visibility of a datapoint on the chart based on user interaction, 
	 * by finding the corresponding series in the chart for the selected datapoint and 
	 * updating its visibility property accordingly, while also preserving the scroll position 
	 * of the chart to ensure a seamless user experience when toggling datapoints on and off, 
	 * and refreshing the chart to reflect the changes in visibility, allowing users to customize 
	 * their view of the data by showing or hiding specific datapoints as needed for analysis or comparison
	 * @param event
	 * @param SeriesID
	 */
	toggleDatapoint(event: any, datapoint: AssetDataPoint) {
		const scrollableDiv = document.getElementById('dockContent');
		const scrollX = scrollableDiv?.scrollLeft || 0;
		const scrollY = scrollableDiv?.scrollTop || 0;

		let item = this.enabledDatapoints.find((x) => x.Datapoint.DataPointID == datapoint.DataPointID);
		if (item) {
			item.Enabled = event.checked;
			let series = this.myChart.series.find((x: SeriesModel) => x.yName == `[${item!.Datapoint.DataPointID}]`);
			if (series) {
				series.visible = event.checked;
				this.myChart.refresh();
			}
		}

		if (scrollableDiv) {
			scrollableDiv.scrollLeft = scrollX;
			scrollableDiv.scrollTop = scrollY;
		}
		if (this.Grid) {
			this.updateGridColumns = true;
			this.gridLoad(true);
		}
	}

	/**
	 * drawSelectedPens
	 * iterates through each series in the chart and checks if it should be drawn based on the selected datapoints, 
	 * by comparing the name of each series to the titles of the selected datapoints and setting the opacity of the series to 0 
	 * if it does not match any of the selected datapoints, effectively hiding it from view on the chart, 
	 * allowing users to focus on specific data points of interest while still retaining the underlying data for analysis or reference
	 */
	drawSelectedPens() {
		this.myChart.series.forEach(series => {
			let shouldDraw: boolean = false;
			this.selectedDatapoints.forEach(datapoint => {
				if (series.name?.includes(datapoint.DPTitle)) {
					shouldDraw = true;
				}
			})
			if (!shouldDraw) {
				series.opacity = 0;
			}
		})
	}

	/**
	 * unzoomChart
	 * resets the zoom level of the chart to its default state, by setting the zoom factor of the primary x-axis to 1 and the zoom position to 0, effectively displaying the entire range of data on the chart without any zoom applied, allowing users to easily return to the original view after zooming in on specific sections of the chart for closer analysis or exploration
	 */
	unzoomChart() {
		if (this.myChart) {
			this.myChart.primaryXAxis.zoomFactor = 1;
			this.myChart.primaryXAxis.zoomPosition = 0;
		}
	}

	/**
	 * onZoom
	 * handles zoom events on the chart, specifically to update the visible start and end dates based on the current zoom level of the chart, 
	 * by checking if the zoom event is occurring on the horizontal axis and if the current zoom factor is not equal to 1, then updating the visible start and end dates
	 * to match the current visible range of the chart, otherwise resetting them to the original start and end dates, while also adjusting the axis interval type and label format based on the number of days in the visible range to ensure that the displayed date range is accurate and appropriately formatted for the zoom level of the chart
	 * @param event
	 */
	onZoom(event: any) {
		if (event.axis.orientation != 'Vertical') {
			if (event.currentZoomFactor != 1) {
				this.visibleStartDate = new Date(event.currentVisibleRange.min);
				this.visibleEndDate = new Date(event.currentVisibleRange.max);
			} else {
				this.visibleStartDate = this.startDate;
				this.visibleEndDate = this.endDate;
			}
			const days = (this.visibleEndDate.getTime() - this.visibleStartDate.getTime()) / 1000 / 60 / 60 / 24;
			event.axis.intervalType = days > 2 ? "Days" : "Hours";
			event.axis.labelFormat = days > 2 ? "dd-MMM" : "HH:mm  dd-MMM";
		}
	}

	/**
	 * onScroll
	 * handles scroll events on the chart, specifically to update the visible start and end dates based on the current 
	 * zoom level and scroll position of the chart, by checking if the scroll event is occurring on the horizontal axis 
	 * and if the zoom factor is not equal to 1, then updating the visible start and end dates to match the current 
	 * visible range of the chart, otherwise resetting them to the original start and end dates, ensuring that the 
	 * displayed date range is accurate and responsive to user interactions with scrolling and zooming on the chart
	 * @param event
	 */
	onScroll(event: any) {
		if (event.axis.orientation != 'Vertical') {
			if (event.zoomFactor != 1) {
				this.visibleStartDate = new Date(event.range.min);
				this.visibleEndDate = new Date(event.range.max);
			} else {
				this.visibleStartDate = this.startDate;
				this.visibleEndDate = this.endDate;
			}
		}
	}

	/**
	 * checkClick
	 * handles click events on the chart, specifically to detect when the user clicks on the zoom reset button, 
	 * by checking if the event target includes the identifier for the zoom reset button and if so, 
	 * calling the zoomComplete method to reset the zoom level of the chart and updating the visible start 
	 * and end dates to match the original start and end dates of the data being displayed, ensuring that the 
	 * chart view is properly reset when the user clicks the zoom reset button	
	 * @param event event
	 */
	checkClick(event: any) {
		if (event.target.toLowerCase().includes("zooming_reset")) {
			this.zoomComplete();
			this.visibleStartDate = this.startDate;
			this.visibleEndDate = this.endDate;
		}
	}

	/**
	 * getMaxValue
	 * returns the maximum value for a given datapoint across the parsed data, by iterating through the parsed data and 
	 * comparing the values for the specified datapoint to find the highest value, which can be used for scaling the 
	 * chart axes or for other analytical purposes
	 * @param datapoint datapoint
	 */
	getMaxValue(datapointID: number): number {
		let result: number = 0;
		let dp = this.getDatapointInAssetDataPoints(datapointID);
		if (dp) {
			for (let i = 0; i < this.parsedData.length; i++) {
				if (this.parsedData[i][`[${dp.DataPointID}]`]) {
					let value: number = <number>this.parsedData[i][`[${dp.DataPointID}]`];
					if (value > result) {
						result = value;
					}
				}
			}
		}
		return result;
	}

	/**
	 * chartHover
	 * handles hover events on the chart, specifically to manage the visibility of strip line text when hovering over rule event 
	 * indicators on the x-axis, by changing the text color to black on hover and reverting it back to transparent when not hovered, 
	 * while also preserving the scroll position of the chart container to prevent unintended scrolling during hover interactions	
	 * @param event event
	 */
	chartHover(event: any) {
		if (this.myChart.primaryXAxis.stripLines) {
			let striplines: StripLineSettingsModel[] = this.myChart.primaryXAxis.stripLines;
			/** If we have hovered over any event stripline */
			if (event.target.includes("stripline_Over_rect_primaryXAxis")) {
				let hoverTime: number = event.axisData["primaryXAxis"];
				let targetStripline: StripLineSettingsModel | undefined;
				striplines.forEach(stripline => {
					if (<number>stripline.start <= hoverTime && <number>stripline.end >= hoverTime) {
						targetStripline = stripline;
					}
				});
				const scrollableDiv = document.getElementById('dockContent');
				const scrollX = scrollableDiv?.scrollLeft || 0;
				const scrollY = scrollableDiv?.scrollTop || 0;

				if (targetStripline && targetStripline.textStyle) {
					targetStripline.textStyle.color = 'black';
				}

				setTimeout(() => {
					if (scrollableDiv) {
						scrollableDiv.scrollLeft = scrollX;
						scrollableDiv.scrollTop = scrollY;
					}
				}, 0);
			} else {
				striplines.forEach(stripline => {
					const scrollableDiv = document.getElementById('dockContent');
					const scrollX = scrollableDiv?.scrollLeft || 0;
					const scrollY = scrollableDiv?.scrollTop || 0;

					if (stripline.textStyle) {
						stripline.textStyle.color = "transparent";
					}

					setTimeout(() => {
						if (scrollableDiv) {
							scrollableDiv.scrollLeft = scrollX;
							scrollableDiv.scrollTop = scrollY;
						}
					}, 0);
				})
			}
		}
	}

	/** onLoading 
	 * event handler for the chart loading event, which can be used to perform actions or checks before the chart is rendered, such as verifying if there are datapoints to display and potentially canceling the loading process if there are none, to prevent rendering an empty chart and improve performance
	*/
	onLoading(event: any) {
		// if (this.noDatapoints) {
		// 	event.cancel = true;
		// }
		// console.log(event);
	}

	/**
	 * onSeriesRender
	 * identifies gaps in the data series that indicate periods where the asset was offline, by checking for time differences greater than 30 minutes between consecutive data points and adding strip lines to the chart to visually indicate these offline periods
	 * this method is called during the series render event to ensure that the offline periods are accurately represented on the chart based on the actual data being displayed 
	 */
	onSeriesRender(event: any) {
		if (this.showOffline) {
			setTimeout(() => {
				let gaps = this.getOfflineGaps(event.series);
				gaps.forEach(gap => {
					let existingStripLines: StripLineSettingsModel[] = this.myChart.primaryXAxis.stripLines ?? [];
					existingStripLines.forEach((stripline: StripLineSettingsModel) => {
						if (gap.start == stripline.start && gap.end == stripline.end) {
							return;
						}
					})
					existingStripLines.push({
						startFromAxis: false,
						start: gap.start,
						end: gap.end,
						zIndex: 'Over',
						opacity: 0.1,
						border: { color: 'salmon', width: 1 },
						isSegmented: true, // Enable the segmented mode for stripline
						segmentAxisName: 'primaryXAxis', // Replace with the axis name if different
						// color: 'salmon',
						// text: ruleEvent.RuleText, // Add tooltip text
						// rotation: -45,
						// verticalAlignment: 'Start',
						// textStyle: { color: 'transparent', fontWeight: 'Bold' }
					})
					this.myChart.primaryXAxis.stripLines = existingStripLines;
				})
			}, 1);
		}
	}

	/**
	 * getOfflineGaps
	 * identifies gaps in the data series that indicate periods where the asset was offline, by checking for time differences greater than 30 minutes between consecutive data points and returning an array of objects with the start and end timestamps of each gap
	 * these gaps can then be plotted on the chart as shaded areas to visually indicate when the asset was offline
	 * this method is called during the series render event to ensure that the offline periods are accurately represented on the chart based on the actual data being displayed
	 * it uses a simple loop to compare timestamps of consecutive data points and builds an array of gap objects that can be used to create strip lines on the chart
	 * @param series series
	 * @returns 
	 */
	getOfflineGaps(series: any): { start: number, end: number }[] {
		let gaps: { start: number, end: number }[] = [];
		let currentPoint: any;
		let previousPoint: any;
		for (let i = 0; i < series.dataSource.length; i++) {
			currentPoint = series.dataSource[i];
			if (i > 0) {
				previousPoint = series.dataSource[i - 1];
				if (currentPoint.timestamp - previousPoint.timestamp > (30 * 60 * 1000)) {
					gaps.push({ start: previousPoint.timestamp, end: currentPoint.timestamp });
				}
			}
		}
		return gaps;
	}

	/**
	 * formatGridData
	 * formats the parsed data into a structure suitable for display in the grid, creating an array of objects where each object represents a row in the grid with a timestamp and values for each datapoint
	 * it also sets up the grid columns based on the datapoints, creating a column for the timestamp and additional columns for each datapoint with appropriate header text
	 */
	formatGridData() {
		this.gridData = this.parsedData.map((x: any) => {
			let item: Record<string, any> = {};
			item["Timestamp"] = this.formatTimestamp(new Date(x.timestamp));
			this.dataPoints.forEach((datapoint: AssetDataPoint) => {
				item['Datapoint_' + datapoint.DataPointID] = x[`[${datapoint.DataPointID}]`] ?? 0;
			})
			return item;
		});

		this.gridColumns = [
			{ field: 'Timestamp', headerText: 'Timestamp' },
			...this.dataPoints.map((dp: AssetDataPoint) => ({
				field: 'Datapoint_' + dp.DataPointID.toString(),
				headerText: dp.DPTitle
			}))
		];
	}

	/**
	 * formatTimestamp
	 * formats a Date object into a string format suitable for display in the grid, including the date, time, and timezone offset
	 * it uses helper functions to pad single digit numbers with leading zeros and to convert the month index into a month name
	 * the timezone offset is calculated from the Date object's timezone offset and formatted as a string with a plus or minus sign followed by the hours offset 
	 * @param date 
	 * @returns 
	 */
	formatTimestamp(date: Date): string {
		const pad = (n: number) => n.toString().padStart(2, '0');

		const day = pad(date.getDate());
		const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
			"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
		const month = monthNames[date.getMonth()];
		const year = date.getFullYear();

		const hours = pad(date.getHours());
		const minutes = pad(date.getMinutes());
		const seconds = pad(date.getSeconds());

		// Timezone offset in hours
		const offset = -date.getTimezoneOffset() / 60;
		const tzSign = offset >= 0 ? '+' : '-';
		const tzHours = pad(Math.floor(Math.abs(offset)));
		const tz = `${tzSign}${tzHours}`;

		return `${day} ${month} ${year} ${hours}:${minutes}:${seconds} ${tz}`;
	}

	/**
	 * toggleTabulate
	 * toggles the tabulate variable that controls whether the grid view is shown or not, 
	 * and sets a flag to update the grid columns when the grid is next loaded if tabulate is being turned on
	 * @param event event 
	 */
	toggleTabulate(event: any) {
		this.tabulate = !this.tabulate;
		if (this.tabulate) {
			this.updateGridColumns = true;
		}
	}

	/**
	 * gridLoad	 
	 * refreshes the grid columns based on the enabledDatapoints array, setting the visibility of each column according to whether the corresponding datapoint is enabled or not
	 * this method is called with a refresh parameter that forces the grid to update its columns when necessary, such as after toggling a datapoint on or off	
	 * @param refresh refresh
	 */
	gridLoad(refresh: boolean = false) {
		if (this.updateGridColumns || refresh) {
			this.Grid.getColumns().forEach((column: any) => {
				let item = this.enabledDatapoints.find((x) => ('Datapoint_' + x.Datapoint.DataPointID.toString()) == column.field);
				if (item) {
					column.visible = item.Enabled;
				}
			})
			this.Grid.refreshColumns();
			this.updateGridColumns = false;
		}
	}

	/**
	 * findDifference
	 * calculates the difference between the first and last value for a given datapoint in the parsed data, used for datapoints with cumulative data to find the total usage over the selected time period
	 * it checks if there are values for the datapoint in both the first and last entry of the parsed data before calculating the difference to avoid errors
	 * @param datapoint datapoint  
	 * @returns 
	 */
	findDifference(datapoint: AssetDataPoint): number {
		let result: number = 0;
		if (this.parsedData.length > 0 && this.parsedData[this.parsedData.length - 1][`[${datapoint.DataPointID}]`] && this.parsedData[0][`[${datapoint.DataPointID}]`]) {
			result = parseInt(this.parsedData[this.parsedData.length - 1][`[${datapoint.DataPointID}]`]!.toString() ?? "0")
				- parseInt(this.parsedData[0][`[${datapoint.DataPointID}]`]!.toString() ?? "0");
		}
		return result;
	}

	/**
	 * getSeries
	 * finds the series in the chart that corresponds to a given datapoint by matching the yName of the series to the DataPointID of the datapoint
	 * @param datapoint datapoint  
	 */
	getSeries(datapoint: AssetDataPoint): SeriesModel | undefined {
		let result = undefined;
		if (this.myChart) {
			result = this.myChart.series.find((x: SeriesModel) => x.yName == `[${datapoint.DataPointID}]`);
		}
		return result;
	}

	@ViewChild('sbDatapoints') sbDatapoints?: SidebarComponent;
	@ViewChild('sbRules') sbRules?: SidebarComponent;
	@ViewChild('togglebtn') togglebtn?: ButtonComponent;

	public showBackdrop: boolean = true;
	public type: string = 'Over';
	public width: string = '280px';
	public closeOnDocumentClick: boolean = true;

	/**
	 * onCreated
	 * handles the onCreated event for the sidebars, ensuring that the sidebar for datapoints is visible when the component is created
	 * @param args args
	 */
	public onCreated(args: any) {
		// (this.sbDatapoints as SidebarComponent).element.style.visibility = '';
	}

	/**
	 * closeClick
	 * handles the click event for the close buttons on the sidebars for datapoints and rules, hiding the respective sidebar when clicked
	 * @param sbComp SidebarComponent
	 */
	closeClick(sbComp: SidebarComponent): void {
		sbComp?.hide();
	};

	/**
	 * btnClick
	 * handles the click event for the buttons that toggle the sidebars for datapoints and rules, ensuring only one sidebar is open at a time	 
	 * @param sidebarNumber sidebarNumber
	 */
	btnClick(sidebarNumber: number): void {
		let sbComp;
		switch (sidebarNumber) {
			case 1:
				this.sbRules?.hide();
				this.sbDatapoints?.toggle();
				break;

			case 2:
				this.sbDatapoints?.hide();
				this.sbRules?.toggle();
				break;
		}
	}

	/**
	 * getUsageValue
	 * calculates a usage value for a datapoint with cumulative data by using the delta value and delta time value provided in the data
	 * the formula is (factor / delta time) * delta value, where factor is used to convert the value to an hourly usage (3600000 ms in an hour)
	 * @param data data 
	 * @returns 
	 */
	getUsageValue(data: ISensorData, datapointID: number): number | undefined {
		let result = undefined;
		let deltaTimestamp = data[`[${datapointID}_DELTA_TIME]`];
		let deltaValue = data[`[${datapointID}_DELTA]`];
		let factor: number = 60 * 60 * 1000; //hour factor
		if (deltaTimestamp && (deltaValue != undefined)) {
			result = (factor / (<number>deltaTimestamp)) * <number>deltaValue;
		}
		return result;
	}

	/**
	 * calculateDeltaChartBarData
	 * creates an array of objects with timestamp and delta value for each window to be used as datasource for the delta bar chart
	 * windows can be hourly or daily depending on the total duration
	 * @param data data 
	 * @param label label 
	 * @returns 
	 */
	calculateDeltaChartBarData(data: ISensorData[], label: string): any[] {
		let result: any[] = [];
		let numWindows: number;
		let hourSeparator: boolean = false;
		const DAY = (24 * 60 * 60 * 1000);
		const HOUR = DAY / 24;
		let numDays: number;
		if (this.strDuration != "custom") {
			numDays = parseInt(this.strDuration.split(":")[0]) || parseInt(this.strDuration.split(":")[1]) / 24;
		} else {
			numDays = (this.endDate.getTime() - this.startDate.getTime()) / 1000 / 60 / 60 / 24;
		}
		numWindows = numDays > 2 ? numDays : numDays * 24;
		hourSeparator = numDays <= 2;

		for (let i: number = 0; i < numWindows; i++) {
			const startDateOnHour = new Date(this.startDate.getTime());
			startDateOnHour.setMinutes(0);
			startDateOnHour.setSeconds(0);
			let start: number = 0;
			if (this.strDuration != "custom") {
				start = startDateOnHour.getTime() + (!hourSeparator ? DAY * (i + 1) : HOUR * (i + 1));
			} else {
				start = i == 0 ? startDateOnHour.getTime() : startDateOnHour.getTime() + (!hourSeparator ? DAY * (i) : HOUR * (i));
			}
			const end = !hourSeparator ? start + DAY : start + HOUR;
			const windowData: number[] = data.filter((x: ISensorData) => x.timestamp > start && x.timestamp <= end).map((x: ISensorData) => <number>x[label]);
			let totalForWindow: number = 0;
			windowData.forEach((x: number) => totalForWindow += x);
			let timestamp = end - (!hourSeparator ? DAY / 2 : HOUR / 2);
			let tsDate: Date = new Date(timestamp);
			if (!hourSeparator) {
				tsDate.setHours(12);
			}
			tsDate.setMinutes(0);
			tsDate.setSeconds(0);
			// timestamp = tsDate.getTime();
			result.push({
				timestamp: this.getBarTimestamp(hourSeparator, start, end),
				sampleDate: new Date(timestamp).toString(),
				[label]: totalForWindow
			});
		}
		return result;
	}

	/**
	 * addSecondXAxis
	 */
	addSecondXAxis() {
		const axis: AxisModel = {
			name: "secondaryXAxis",
			labelIntersectAction: "Wrap",
			valueType: 'Category',
			labelRotation: Browser.isDevice ? -45 : 0,
			opposedPosition: true,
		}
		this.myChart.addAxes([axis]);
	}

	/**
	 *	getBarTimestamp
	 * @param hourSeparator hourSeparator
	 * @param start start timestamp
	 * @param end end timestamp
	 * @returns 
	 */
	getBarTimestamp(hourSeparator: boolean, start: number, end: number): string {
		let result: string = "";
		const startDate = new Date(start);
		const endDate = new Date(end);
		result = hourSeparator
			? `${startDate.getDate()} 
			${this.shortMonths[startDate.getMonth()]} 
			${startDate.getHours().toString().padStart(2, '0')}:00 
			- 
			${end <= new Date().getTime() ? endDate.getHours().toString().padStart(2, '0') + ':00' : 'Now'}`
			: `${startDate.toDateString()}`;
		return result;
	}

	/**
	 * toggleShowBars
	 * @param event event 
	 */
	toggleShowBars(event: any) {
		this.showDeltaAsBar = !this.showDeltaAsBar;
		let series: SeriesModel[] = this.myChart.series.filter((x: SeriesModel) => x.yName?.includes("_DELTA"));
		let yAxisName: string = "";
		series.forEach((series: SeriesModel) => {
			series.type = this.showDeltaAsBar ? "Column" : "Line";
			series.yName = this.showDeltaAsBar ? `${series.yName?.split("_")[0]}_DELTA]` : `${series.yName?.split("_")[0]}_DELTA_CALCULATED]`;
			if (this.showDeltaAsBar) {
				const yName = series.yName;
				const barData = this.barData.find((x) => x.datapointID == yName.replace("[", "").replace("]", ""));
				if (barData) {
					series.dataSource = barData.data;
				}
			} else {
				series.dataSource = this.parsedData;
			}
			series.xAxisName = this.showDeltaAsBar ? "secondaryXAxis" : "primaryXAxis";
			series.opacity = this.showDeltaAsBar ? 0.75 : 1;
			yAxisName = series.yAxisName ?? "";
			series.name = this.showDeltaAsBar ? series.name?.replaceAll("Running", "Usage") : series.name?.replaceAll("Usage", "Running");
		});
		if (yAxisName != "") {
			let secondaryYAxis = this.myChart.axes.find((x: AxisModel) => x.name == yAxisName);
			if (secondaryYAxis) {
				if (this.showDeltaAsBar) {
					const max: number = Math.max(...this.barData.map((x) => x.max));
					const min: number = Math.min(...this.barData.map((x) => x.min));
					const interval = (max - min) / 10;
					secondaryYAxis.maximum = max + interval;
					secondaryYAxis.minimum = 0;
					secondaryYAxis.interval = interval;
				} else {
					const max: number = Math.max(...this.calculatedData.map((x) => x.max));
					const min: number = Math.min(...this.calculatedData.map((x) => x.min));
					const interval = (max - min) / 10;
					secondaryYAxis.maximum = max + interval;
					secondaryYAxis.minimum = (min - interval) < 0 ? 0 : (min - interval);
					secondaryYAxis.interval = interval;
				}
			}
		}
		this.myChart.refresh();
	}

	/**
	 * checkFeature
	 * Checks if the selected account has a specific feature enabled.
	 * @param feature feature   
	 * @returns 
	 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.selectedAccount && this.selectedAccount.Features) {
			return AccountsService.checkFeature(feature, this.selectedAccount)
		}
		return false;
	}

	/**
	 * getVirtualSensors
	 * @param asset asset 
	 */
	getVirtualSensors(asset: LevelAsset) {
		this.assetVirtualSensors = [];
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.virtualSensorsService.getVirtualSensor$(this.selectedAccountUID, this.selectedOrganisationUID, 0, asset.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((virtualSensors) => !!virtualSensors),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (virtualSensors) => {
				this.assetVirtualSensors = virtualSensors;
				this.assetVirtualSensors.forEach((vs: VirtualSensor) => {
					vs._dataPoints = JSON.parse(vs.DataPoints ?? '[]').map((x: DataPoint) => (
						{
							value: `[${this.getValueForDP(x, vs)}]`,
							datapoint: x
						}
					));
				});
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.virtualSensorsService.getVirtualSensor$", false);
				}
			},
		});
	}

	/**
	 * getVirtualSensorValues
	 */
	async getVirtualSensorValues() {
		this.virtualSensorData = [];
		let result: ISensorData[] = [];
		for (let i: number = 0; i < this.assetVirtualSensors.length; i++) {
			const virtualSensor: VirtualSensor = this.assetVirtualSensors[i];
			const inputDPs = JSON.parse(virtualSensor.InputDataPoints);
			let idString: string = "";
			Object.entries(inputDPs).forEach((item) => idString += `${item[1]},`);
			idString = idString.slice(0, idString.length - 1);
			const inputDatapoints: DataPoint[] = await this.GetDataPointsFromID(this.selectedAccountUID, this.selectedOrganisationUID, idString);
			const inputDPData = await this.GetDataPointValuesFromIDs(this.selectedAsset, this.startDate.getTime(), this.endDate.getTime(), idString);

			let valuesToUse: ISensorData[] = inputDPData;

			if (inputDatapoints.length > 1) {
				// if all sensors are cumulative and we want to use the raw value then we can interpolate missing values
				let isCumulative: boolean = true;
				inputDatapoints.forEach((x: DataPoint) => { if (!x.IsCumulative) isCumulative = false });
				if (isCumulative) {
					valuesToUse = await this.interpolateValues(inputDPData, inputDatapoints);
				} else {
					valuesToUse = await this.bucketData(inputDPData, inputDatapoints);
				}
			}
			let conversion = virtualSensor.Conversion;
			Object.entries(inputDPs).forEach((item) => conversion = conversion.replaceAll(`[${item[0]}]`, `[${item[1]}]`));
			for (let j: number = 1; j < valuesToUse.length; j++) {
				const interpolatedVal = valuesToUse[j];
				let valConversion = conversion;
				Object.entries(interpolatedVal).forEach((item) => {
					valConversion = valConversion.replaceAll(item[0], `${item[1]}`);
				});
				let _func = Function(`return ${valConversion}`);
				const resultValue = _func();
				// const resultValue = eval(valConversion);
				let notValid: boolean = false;
				if (valConversion.includes("[") || valConversion.includes("]") || Number.isNaN(resultValue)) {
					notValid = true;
				}
				if (!notValid) {
					const resultItem: ISensorData = {
						[virtualSensor.VirtualSensorID]: resultValue,
						timestamp: interpolatedVal.timestamp,
						sampleDate: interpolatedVal.sampleDate
					};
					if (!Number.isNaN(resultItem[virtualSensor.VirtualSensorID])) {
						result.push(resultItem);
					}
				}
			}

			result = result.slice(0, result.length - 1);

			let axisName: string = "";

			let SIs = new Set(inputDatapoints.map((x: DataPoint) => x.SI));
			if (virtualSensor.SI) {
				SIs = new Set([virtualSensor.SI]);
			}
			if (SIs.size == 1) { // if we only have one si
				const si = SIs.values().next().value;
				const axis = [this.myChart.primaryYAxis, ...this.myChart.axes].find((x: AxisModel) => x.name == `Axis${si}`);
				if (axis) {
					const nums = result.map((item: ISensorData) => <number>item[`${virtualSensor.VirtualSensorID}`]).filter((x: number) => x != undefined);
					const max = Math.max(...nums);
					const min = Math.min(...nums);
					if (!Number.isNaN(max) && max > (<number>axis.maximum - <number>axis.interval)) {
						axis.maximum = max + <number>axis.interval;
						axis.interval = (<number>axis.maximum - <number>axis.minimum) / 10;
					}
					if (!Number.isNaN(min) && min < (<number>axis.minimum - <number>axis.interval)) {
						axis.minimum = min - <number>axis.interval;
						axis.interval = (<number>axis.maximum - <number>axis.minimum) / 10;
					}
					axisName = axis.name!
				}
				else {
					const si = SIs.values().next().value;
					const axis: AxisModel = {
						name: `Axis${si}`,
						labelIntersectAction: "Wrap",
						labelFormat: `{value} ${si}`,
						// valueType: 'Category',
						// intervalType: parseInt(this.strDuration.split(":")[0]) > 2
						// 	|| (this.strDuration == 'custom' && (this.endDate.getTime() - this.startDate.getTime() >= (3 * 24 * 60 * 60 * 1000)))
						// 	? "Days" : "Hours",
						// labelFormat: parseInt(this.strDuration.split(":")[0]) > 2
						// 	|| (this.strDuration == 'custom' && (this.endDate.getTime() - this.startDate.getTime() >= (3 * 24 * 60 * 60 * 1000)))
						// 	? "dd-MMM" : "HH:mm  dd-MMM",
						// labelRotation: Browser.isDevice ? -45 : 0,
						opposedPosition: true,
						// labelFormat: this.myChart.primaryXAxis.labelFormat,
						// intervalType: this.myChart.primaryXAxis.intervalType
					}
					this.myChart.addAxes([axis]);
					axisName = axis.name!;
				}
			}

			if (SIs.size > 1) {
				const axis: AxisModel = {
					name: `Axis${virtualSensor.VirtualSensorID}`,
					labelIntersectAction: "Wrap",
					// valueType: 'Category',
					// intervalType: parseInt(this.strDuration.split(":")[0]) > 2
					// 	|| (this.strDuration == 'custom' && (this.endDate.getTime() - this.startDate.getTime() >= (3 * 24 * 60 * 60 * 1000)))
					// 	? "Days" : "Hours",
					// labelFormat: parseInt(this.strDuration.split(":")[0]) > 2
					// 	|| (this.strDuration == 'custom' && (this.endDate.getTime() - this.startDate.getTime() >= (3 * 24 * 60 * 60 * 1000)))
					// 	? "dd-MMM" : "HH:mm  dd-MMM",
					// labelRotation: Browser.isDevice ? -45 : 0,
					opposedPosition: true,
					// labelFormat: this.myChart.primaryXAxis.labelFormat,
					// intervalType: this.myChart.primaryXAxis.intervalType
				}
				this.myChart.addAxes([axis]);
				axisName = axis.name!;
			}

			let seriesColor = this.defaultColors[this.defaultColorIndex];
			this.defaultColorIndex = (this.defaultColorIndex + 1) % this.defaultColors.length;

			const seriesName: string = virtualSensor.SI ? `${virtualSensor.VirtualSensorName} (${virtualSensor.SI})` : `${virtualSensor.VirtualSensorName}`;

			this.myChart.addSeries([{
				animation: {
					enable: false
				},
				emptyPointSettings: { mode: "Drop" },
				type: "Line",
				dataSource: result,
				name: seriesName,
				xName: 'timestamp',
				yName: `${virtualSensor.VirtualSensorID}`,
				marker: {
					visible: false,
					width: 1,
					height: 8,
					shape: 'Triangle',
					dataLabel: { "name": 'x' },
					isFilled: true
				},
				yAxisName: axisName,
				// xAxisName: "primaryXAxis",
				fill: seriesColor,
				width: 1
			}]);
		}
		this.virtualSensorData = result;
	}

	/**
	 * interpolateValues
	 * @param data data 
	 * @param datapoints datapoints 
	 */
	async interpolateValues(data: ISensorData[], datapoints: DataPoint[]): Promise<ISensorData[]> {
		return new Promise<ISensorData[]>((resolve, reject) => {
			const result: ISensorData[] = [...data];
			for (let i: number = 1; i < data.length; i++) {
				let dataItem = data[i];
				const label = Object.entries(dataItem).find((item) => this.regex.test(item[0]))?.[0];
				if (label) {
					const otherData = data.find((x: ISensorData) => x.timestamp == dataItem.timestamp && !x[label]) ?? [];
					if (otherData.length != datapoints.length) {
						datapoints.forEach((dp: DataPoint) => {
							const dpLabel = `[${dp.DataPointID}]`;
							const pointAfter = this.getFirstPointAfter(data, dpLabel, dataItem.timestamp);
							const timeDifference = pointAfter.timestamp - dataItem.timestamp;
							if (timeDifference > 0) {
								const percentage = (timeDifference / (<number>pointAfter[`${dpLabel.replace("]", "")}_DELTA_TIME]`]));
								const differenceFromTimestamp = <number>pointAfter[`${dpLabel.replace("]", "")}_DELTA]`] * percentage;
								const interpolatedValue = <number>pointAfter[dpLabel] - differenceFromTimestamp;
								dataItem[dpLabel] = interpolatedValue;
							}
						});
					}
				}
			}
			// const filteredResult = result
			// 	.filter((x: ISensorData) => result
			// 		.filter((y: ISensorData) => x.timestamp == y.timestamp).length == datapoints.length);
			resolve(result);
		});
	}

	/**
	 * bucketData
	 * @param data data 
	 * @param datapoints datapoints 
	 * @returns 
	 */
	async bucketData(data: ISensorData[], datapoints: DataPoint[]): Promise<ISensorData[]> {
		return new Promise<ISensorData[]>((resolve, reject) => {
			let result: ISensorData[] = [];
			let numWindows: number;
			const startEpoch = this.startDate.getTime();
			const endEpoch = this.endDate.getTime();
			const DAY = (24 * 60 * 60 * 1000);
			const HOUR = DAY / 24;
			const TENMINS = HOUR / 6;
			let separator = HOUR;
			let numDays: number;
			if (this.strDuration != "custom") {
				numDays = parseInt(this.strDuration.split(":")[0]) || parseInt(this.strDuration.split(":")[1]) / 24;
			} else {
				numDays = (endEpoch - startEpoch) / 1000 / 60 / 60 / 24;
			}
			numWindows = numDays * 24; //hour interval if over 7 days
			if (numDays < 6.5) {
				numWindows *= 6; //10 minute interval if under 7 days
				separator = TENMINS;
			}
			for (let i: number = 0; i < numWindows; i++) {
				const start = (startEpoch + (separator * i));
				const end = start + separator;
				const timestamp = start + ((end - start) / 2);
				let dataItem: ISensorData = {
					timestamp: timestamp,
					sampleDate: new Date(timestamp).toString()
				}
				datapoints.forEach((datapoint: DataPoint) => {
					const windowValues = data.filter((x: ISensorData) => x.timestamp >= start && x.timestamp <= end)
						.map((x: ISensorData) => <number>x[`[${datapoint.DataPointID}]`]).filter((x: number) => x != undefined);
					if (windowValues.length > 0) {
						const avgVal = (windowValues.reduce((sum, val) => sum + val, 0) / windowValues.length);
						dataItem[`[${datapoint.DataPointID}]`] = avgVal;
					}
				});
				result.push(dataItem);
			}
			resolve(result);
		});
	}

	/**
	 * getFirstPointAfter
	 * @param data data 
	 * @param label label 
	 * @param timestamp timestamp 
	 * @returns 
	 */
	getFirstPointAfter(data: ISensorData[], label: string, timestamp: number): ISensorData {
		const filteredData = data.filter((x: ISensorData) => x[label] != undefined);
		const exactMatch = filteredData.find((x: ISensorData) => x.timestamp == timestamp);
		if (exactMatch) return exactMatch;
		for (let i: number = 1; i < filteredData.length; i++) {
			if (filteredData[i].timestamp > timestamp) {
				return filteredData[i];
			}
		}
		return filteredData[filteredData.length - 1];
	}

	/**
	 * getClosestData
	 * @param data data 
	 * @param label label 
	 * @returns 
	 */
	getClosestData(data: ISensorData[], label: string, timestamp: number): { before: any, after: any } {
		let result: { before: any, after: any } = { before: undefined, after: undefined };
		const filteredData = data.filter((x: ISensorData) => x[label] != undefined);
		const exactMatch = filteredData.find((x: ISensorData) => x.timestamp == timestamp);
		if (exactMatch) return { before: exactMatch, after: exactMatch };
		for (let i: number = 1; i < filteredData.length; i++) {
			if (filteredData[i].timestamp > timestamp) {
				result = {
					before: filteredData[i - 1],
					after: filteredData[i]
				};
				break;
			}
		}
		return result;
	}

	/**
	 * GetDataPointsFromID
	 * @param accountID accountID 
	 * @param orgUID orgUID 
	 */
	async GetDataPointsFromID(accountID: string, orgUID: string, ids: string): Promise<DataPoint[]> {

		return new Promise((resolve, reject) => {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.datapointsDataService.getDataPoints$(accountID, orgUID, ids).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((dataPoints) => !!dataPoints),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (dataPoints: any) => {
					this.virtualSensorInputDataPoints = dataPoints;
					resolve(dataPoints);
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getDataPoints$", false);
					}
					reject();
				},
				//complete: () => { console.log("this.AssetsDataService.getDataPoints$.subscribe: Complete"); }
			});
		});
	}

	/**
	 * GetDataPointValuesFromIDs
	 * @param asset asset 
	 * @param dateStart dateStart 
	 * @param dateEnd dateEnd 
	 * @param datapointIDs datapointIDs 
	 * @returns 
	 */
	async GetDataPointValuesFromIDs(asset: LevelAsset, dateStart: any, dateEnd: any, datapointIDs: string): Promise<ISensorData[]> {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		return new Promise<ISensorData[]>((resolve, reject) => {
			this.AssetsDataService.getDataPointValues$(this.selectedAccountUID, this.selectedOrganisationUID, dateStart, dateEnd, datapointIDs).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				// filter((dataPointValues) => !!dataPointValues),
				tap(() => {
				}),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.displaySpinner(false);
					this.enableChartRefresh();
				})
			).subscribe({
				next: (dataPointValues: any) => {
					this.myChart.primaryXAxis.stripLines = [];
					try {
						let parsedData: ISensorData[];
						parsedData = dataPointValues.map((dataPointValue: any) => {
							return {
								//AssetId: dataPointValue.AssetID,
								AssetId: asset.AssetID,
								timestamp: dataPointValue.Timestamp,
								sampleDate: new Date(dataPointValue.Timestamp),
								...dataPointValue.Values,
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaValues).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA" + "]"]: value };
									})
								),
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaTimestamps).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA_TIME" + "]"]: value };
									})
								)
							}
						});

						parsedData.forEach((x: ISensorData) => {
							const DPID = parseInt(Object.entries(x).find((y) => this.regex.test(y[0]))?.[0].replace("[", "").replace("]", "") ?? "");
							if (DPID) {
								x[`[${DPID}_DELTA_CALCULATED]`] = (<number>this.getUsageValue(x, DPID)) ?? 0;
							}
						});
						resolve(parsedData);

					} catch (error) {
						console.error(error);
						reject();
					}
				},
				error: (error) => {
					this.dataPointValues = [];
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPointValues$", false)
					reject();
				},
				//complete: () => { console.log("this.AssetsDataService.getAssetDataPointValues$.subscribe: Complete"); }
			});
		});
	}

	/* addCmmsCase
	 * Opens a dialog to create a CMMS case file based on the selected asset and user input, then posts the new case file to the CMMS service.
	 */
	addCmmsCase() {

		if ((this.selectedHierarchicalLocation.FunctionalLocation == "" && this.selectedAsset.FunctionalLocation == "") || this.selectedAsset.EquipmentCode == "") {
			this.globalFunctions.UserConfirm$('This Asset needs a Functional Location and an Equipment Code').subscribe(OKResult => {
				return;
			});
			return;
		}

		let dialogData = <createCMMSDialogData>{
			assetItem: this.selectedAsset,
			parent: this.selectedHierarchicalLocation,
			isAttachedtoEvent: false
		}

		const dialogRef = this.dialog.open(CreateCmmsCaseComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe(result => {
			if (result) {
				let newCaseFile: ICaseFile = {} as ICaseFile;
				newCaseFile.AssetID = this.selectedAsset.AssetID;
				newCaseFile.TriggerType = result.TriggerType;
				newCaseFile.Description = result.Description;
				newCaseFile.OpenedAt = new Date().getTime();
				newCaseFile.OrganisationUID = this.selectedOrganisationUID;
				newCaseFile.UserID = result.UserID;
				let disable = (result.RuleList && result.RuleList.length > 0);

				this.cmmsService.postCase$(this.selectedAccountUID, this.selectedOrganisationUID, newCaseFile).pipe(
					take(1), distinctUntilChanged(),
					filter((caseFile) => !!caseFile)
				).subscribe({
					next: (caseFile) => {
						this.globalFunctions.DisplaySnackBar({ message: "CMMS Case File Created", type: SnackType.Info, duration: 1000 });
					}
				});

			}
		});

	}

	/**
	 * getValueForDP
	 * @param datapoint datapoint 
	 */
	getValueForDP(datapoint: DataPoint, virtualSensor: VirtualSensor): number {
		return parseInt(Object.entries(JSON.parse(virtualSensor.InputDataPoints)).find((item) => item[1] == `${datapoint.DataPointID}`)?.[0] ?? "0");
	}
}
